import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, VerifyData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar, Grid, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useMediaQuery
} from '@mui/material';
import {
  Search, Add, ShoppingCart, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, ExpandMore,
  ExpandLess, CheckCircleOutline, Assignment, Visibility, Edit, Delete
} from '@mui/icons-material';
import SaleModal from './SaleModal';
import VerifySaleModal from './VerifySaleModal';
import PaymentReceiptsModal from '../PaymentReceipts/PaymentReceiptsModal';
import Pagination from '../../common/Pagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import MampillyLogo from '../Logo/logo_Madavana.png';
import { FaWhatsapp } from 'react-icons/fa';
import './SaleIndex.css';

const SaleIndex = () => {
  // Session validation
  useSessionValidation();

  const { companyId, branchId, token, userId, permissions } = useAuth();
  const { logAudit } = useAuditLog();

  const hasCreateMasterPermission = permissions?.includes('Create Master');
  const hasEditMasterPermission = permissions?.includes('Edit Master');
  const hasDeleteMasterPermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null); // eslint-disable-line no-unused-vars

  // Ref to reliably track SaleOrderId across async state updates
  const saleOrderIdRef = useRef(null);

  // Verify modal states
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifySaleData, setVerifySaleData] = useState(null);
  const [verifySaleDetails, setVerifySaleDetails] = useState([]);

  // Orders (Confirmed Enquiries) modal
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [confirmedOrders, setConfirmedOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersCount, setOrdersCount] = useState(0);

  // Data states
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const isMobile = useMediaQuery('(max-width:768px)');
  const [showSearch, setShowSearch] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };


  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [saleDetails, setSaleDetails] = useState([]);
  const [saleBundleDetails, setSaleBundleDetails] = useState([]);
  const [saleStockBranchDetails, setSaleStockBranchDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Payment Status popup
  const [paymentPopupAnchor, setPaymentPopupAnchor] = useState(null);
  const [paymentPopupData, setPaymentPopupData] = useState(null);

  // Receipt View Modal
  const [receiptViewModal, setReceiptViewModal] = useState({ open: false });
  const [receiptViewFormData, setReceiptViewFormData] = useState({});
  const [receiptViewDetails, setReceiptViewDetails] = useState([]);
  const [receiptViewLoading, setReceiptViewLoading] = useState(false);

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Company data for PDF
  const [companyData, setCompanyData] = useState(null);
  // Branch data for PDF address/contact
  const [branchData, setBranchData] = useState(null);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
      fetchOrdersCount();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch functions
  const fetchSales = async () => {
    try {
      const activeResponse = await fetchData('SaleApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true, IsHold: item.IsHold ?? false }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('SaleApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive sales:', inactiveError);
      }

      const allSales = [...activeData, ...inactiveData];
      setSales(allSales);

      // Fetch IsHold for each sale from detail API in parallel
      const holdResults = await Promise.allSettled(
        allSales.map(s => fetchData('SaleApi', `/${s.Id}`, token).then(r => {
          const d = r.response_data || r;
          const sd = d.Sale || d;
          return { id: s.Id, isHold: sd.IsHold || false };
        }))
      );
      const holdMap = {};
      holdResults.forEach(r => { if (r.status === 'fulfilled') holdMap[r.value.id] = r.value.isHold; });
      setSales(prev => prev.map(s => ({ ...s, IsHold: holdMap[s.Id] ?? s.IsHold })));

    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      setCustomers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const fetchItems = async (masterId = 0) => {
    try {
      const response = await fetchData('SaleApi', `/ItemStockListDetails/ItemStockListDetails/${companyId}/${branchId}/${branchId}/${masterId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await fetchData('BankApi', `/${companyId}`, token);
      setBanks(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const fetchMaxBillNo = async (invoiceTypeId = null) => {
    try {
      const invoiceTypeParam = invoiceTypeId ? `/${invoiceTypeId}` : '';
      const response = await fetchData('SaleApi', `/MaxBillno/${companyId}/${branchId || 1}${invoiceTypeParam}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';

      console.log('Max Bill Data:', { billNumber, billPrefix, finYear, rawData: data });

      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('SaleOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || [];
      let freightData = null, packingData = null;
      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing'));
      }
      setFreightPackingGST({ freight: freightData, packing: packingData });
      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token);
      setCompanyData(response.response_data || response || null);
    } catch (error) {
      console.error('Error fetching company data:', error);
      setCompanyData(null);
    }
  };

  const fetchBranchData = async () => {
    try {
      const response = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const data = response.response_data || response || null;
      setBranchData(data);
    } catch (error) {
      console.error('Error fetching branch data:', error);
      setBranchData(null);
    }
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setLoadingMessage('Loading sales...');
    try {
      await Promise.allSettled([
        fetchSales(),
        fetchCustomers(),
        fetchItems(),
        fetchBanks(),
        fetchFreightAndPackingGST(),
        fetchCompanyData(),
        fetchBranchData(),
        fetchConvertedSOList().then(soList => setRequestCount(soList.length))
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, branchId, token, fromDate, toDate]);

  const fetchSingleSale = async (saleId) => {
    try {
      const response = await fetchData('SaleApi', `/${saleId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single sale:', error);
      return null;
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    if (action === 'view') {
      const recordId = typeof itemOrId === 'object' ? (itemOrId?.Id || 0) : (itemOrId || 0);
      logAudit(AUDIT_ACTION.VIEW, 'SaleApi', recordId);
    }
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);
      const freshData = await fetchSingleSale(itemId);
      await fetchItems(freshData?.Sale?.MasterId || freshData?.MasterId || 0); // Refresh stock on open

      if (freshData) {
        const saleData = freshData.Sale || freshData;
        const details = freshData.SaleDetails || [];

        // Update IsHold in the sales list so row color reflects immediately
        setSales(prev => prev.map(s => s.Id === saleData.Id ? { ...s, IsHold: saleData.IsHold || false } : s));

        setSelectedItem(saleData);
        setFormData({
          Id: saleData.Id,
          CompanyId: saleData.CompanyId || companyId,
          BranchId: saleData.BranchId || branchId,
          MasterId: saleData.MasterId,
          CustomerName: saleData.CustomerName || '',
          GSTNumber: saleData.GSTNumber || '',
          InvoiceTypeId: saleData.InvoiceTypeId ?? null,
          InvoiceTypeName: saleData.InvoiceTypeName || '',
          StateId: saleData.StateId ?? null,
          SalesManId: saleData.SalesManId ?? null,
          SalesManName: saleData.SalesManName || '',
          BillNumber: saleData.BillNumber,
          BillPrefix: saleData.BillPrefix || '',
          FinYear: saleData.FinYear || '',
          BillDate: saleData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          ClearingDate: saleData.ClearingDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          GstCalculationType: saleData.GstCalculationType || 2,
          GstCalculationTypeName: saleData.GstCalculationTypeName || 'Including Tax',
          Amount: saleData.Amount || 0,
          DiscountAmount: saleData.DiscountAmount || 0,
          TaxableAmount: saleData.TaxableAmount || 0,
          CGSTAmount: saleData.CGSTAmount || 0,
          SGSTAmount: saleData.SGSTAmount || 0,
          IGSTAmount: saleData.IGSTAmount || 0,
          FreightAmount: saleData.FreightAmount || 0,
          FreightGSTRate: saleData.FreightGSTRate || 0,
          FreightGSTAmount: saleData.FreightGSTAmount || 0,
          PackingAmount: saleData.PackingAmount || 0,
          PackingGSTRate: saleData.PackingGSTRate || 0,
          PackingGSTAmount: saleData.PackingGSTAmount || 0,
          TCSAmount: saleData.TCSAmount || 0,
          RoundOff: saleData.RoundOff || 0,
          TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
          GrandTotal: saleData.GrandTotal || saleData.TotalAmount || 0,
          CashAmount: saleData.CashAmount || 0,
          UPIAmount: saleData.UPIAmount || 0,
          CardAmount: saleData.CardAmount || 0,
          BankId: saleData.BankId || null,
          BalanceAmount: saleData.BalanceAmount || 0,
          Remarks: saleData.Remarks || '',
          // Shipping & Vehicle Details
          DispatchFrom: saleData.DispatchFrom || '',
          DispatchAddress1: saleData.DispatchAddress1 || '',
          DispatchAddress2: saleData.DispatchAddress2 || '',
          DispatchLocation: saleData.DispatchLocation || '',
          DispatchPincode: saleData.DispatchPincode || '',
          ShipTo: saleData.ShipTo || '',
          ShipGSTNo: saleData.ShipGSTNo || '',
          ShipToAddress1: saleData.ShipToAddress1 || '',
          ShipToAddress2: saleData.ShipToAddress2 || '',
          ShipToLocation: saleData.ShipToLocation || '',
          ShipToPincode: saleData.ShipToPincode || '',
          CustomerContactNumber: saleData.CustomerContactNumber || saleData.ContactNumber || '',
          VehicleNumber: saleData.VehicleNumber || '',
          TransporterName: saleData.TransporterName || '',
          TransporterGST: saleData.TransporterGST || '',
          Distance: saleData.Distance || 0,
          IsVerified: saleData.IsVerified || false,
          IsHold: saleData.IsHold || false,
          IsActive: saleData.IsActive ?? true,
          IsOnSite: saleData.IsOnSite ?? false,
          IsInstallation: saleData.IsInstallation ?? false,
          InstallationDate: saleData.InstallationDate ? saleData.InstallationDate.split('T')[0] : '',
          IsCashOnDelivery: saleData.IsCashOnDelivery ?? false,
          IsExchange: saleData.IsExchange ?? false,
          ExchangeItemName: saleData.ExchangeItemName || '',
          ExchangeAmount: saleData.ExchangeAmount || 0,
          SaleOrderId: saleData.SaleOrderId ?? null,
          EnquiryId: saleData.EnquiryId ?? null,
          CreatedBy: saleData.CreatedBy,
          CreatedDate: saleData.CreatedDate,
          CreatedByName: saleData.CreatedByName,
          ModifiedBy: saleData.ModifiedBy,
          ModifiedDate: saleData.ModifiedDate,
          ModifiedByName: saleData.ModifiedByName
        });
        // Sync ref with edit mode SaleOrderId
        saleOrderIdRef.current = saleData.SaleOrderId ?? null;
        const mappedDetails = details.map((d, idx) => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          ItemName: d.ItemName || items.find(i => i.Id === parseInt(d.ItemId))?.Name || '',
          _rowKey: `row_${Date.now()}_${Math.random()}`,
          _rowIndex: idx,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          MinRate: d.MinRate || 0,
          MaxRate: d.MaxRate || 0,
          BalanceStock: parseFloat(items.find(i => i.Id === parseInt(d.ItemId))?.CurrentBranchStock ?? items.find(i => i.Id === parseInt(d.ItemId))?.BalanceStock) || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          BatchNumber: d.BatchNumber || '',
          BatchId: d.BatchId || 0,
          HasWarranty: d.HasWarranty || false,
          IsInstallation: d.IsInstallation ?? false,
          BilledQuantity: d.BilledQuantity || d.Quantity || 0,
          OrderQuantity: d.OrderQuantity || d.Quantity || 0,
          StockQuantity: d.StockQuantity || 0,
          DamageQuantity: d.DamageQuantity || 0,
          ExcessQuantity: d.ExcessQuantity || 0,
          LessQuantity: d.LessQuantity || 0,
          ReturnDamageQuantity: d.ReturnDamageQuantity || 0,
          ReturnStockQuantity: d.ReturnStockQuantity || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy,
          CreatedDate: d.CreatedDate,
          ModifiedBy: d.ModifiedBy,
          ModifiedDate: d.ModifiedDate,
          PrevQuantity: d.PrevQuantity,
        }));
        setSaleDetails(mappedDetails);

        // Auto-set IsInstallation on formData if any detail has it
        const anyInstallation = mappedDetails.some(d => !!(d.IsInstallation));
        if (anyInstallation) {
          setFormData(prev => ({ ...prev, IsInstallation: true }));
        }

        // Populate bundle details from API response, mapping _parentRowIndex by ItemId
        const rawBundleDetails = freshData.SaleBundleDetails || freshData.BundleDetails || [];
        if (rawBundleDetails.length > 0) {
          const mappedBundles = rawBundleDetails.map(b => {
            // Find which parent row index this bundle item belongs to
            const parentIndex = mappedDetails.findIndex(d => parseInt(d.ItemId) === parseInt(b.ItemId));
            return {
              _parentRowIndex: parentIndex !== -1 ? parentIndex : 0,
              _parentItemId: parseInt(b.ItemId) || null,
              ItemId: b.ItemId || null,
              ProductId: b.ProductItemId || b.ProductId || null,
              ProductItemId: b.ProductItemId || null,
              ItemName: b.ItemName || '',
              ProductName: b.ProductName || '',
              Quantity: parseFloat(b.Quantity) || 0,
              Rate: parseFloat(b.Rate) || 0,
              Amount: parseFloat(b.Amount) || 0,
              BalanceStock: b.BalanceStock != null ? parseFloat(b.BalanceStock) : null,
              IsActive: b.IsActive !== undefined ? b.IsActive : true,
              CreatedBy: b.CreatedBy || null,
              ModifiedBy: b.ModifiedBy || null,
              CreatedDate: b.CreatedDate || null,
              ModifiedDate: b.ModifiedDate || null,
            };
          });
          setSaleBundleDetails(mappedBundles);
        } else {
          setSaleBundleDetails([]);
        }

        // Populate stock branch details from API response
        const rawStockBranchDetails = freshData.SaleStockBranchDetails || [];
        if (rawStockBranchDetails.length > 0) {
          const mappedStockBranch = rawStockBranchDetails.map(sb => {
            const parentIndex = mappedDetails.findIndex(d => d.Id === sb.SaleDetailsId || parseInt(d.ItemId) === parseInt(sb.ItemId));
            return {
              _parentRowIndex: parentIndex !== -1 ? parentIndex : 0,
              Id: sb.Id || 0,
              SaleId: sb.SaleId || 0,
              SaleDetailsId: sb.SaleDetailsId || 0,
              BranchId: sb.BranchId || null,
              BranchName: sb.BranchName || '',
              ItemId: sb.ItemId || 0,
              Quantity: parseFloat(sb.Quantity) || 0,
              IsActive: sb.IsActive ?? true,
            };
          });
          setSaleStockBranchDetails(mappedStockBranch);

          // Also set _branchSplits on the detail rows for re-saving
          const updatedMapped = mappedDetails.map((d, idx) => {
            const detailSplits = mappedStockBranch.filter(sb => sb._parentRowIndex === idx);
            if (detailSplits.length > 0) {
              return {
                ...d,
                _branchSplits: detailSplits.map(sb => ({
                  branchId: sb.BranchId,
                  branchName: sb.BranchName || '',
                  qty: sb.Quantity,
                  stock: null
                }))
              };
            }
            return d;
          });
          setSaleDetails(updatedMapped);
        } else {
          setSaleStockBranchDetails([]);
        }
      }
      setModalLoading(false);
    } else {
      // Create mode
      setModalLoading(true);
      const gstData = await fetchFreightAndPackingGST();
      await fetchItems(0); // Refresh stock data on modal open

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        MasterId: '',
        InvoiceTypeId: 0,
        BillNumber: '', // Will be set when invoice type is selected
        BillPrefix: '',
        FinYear: '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        GstCalculationType: 2,
        GstCalculationTypeName: 'Including Tax',
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        FreightAmount: 0,
        FreightGSTRate: parseFloat(gstData.freight?.GSTRate) || 0,
        FreightGSTAmount: 0,
        PackingAmount: 0,
        PackingGSTRate: parseFloat(gstData.packing?.GSTRate) || 0,
        PackingGSTAmount: 0,
        TCSAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        GrandTotal: 0,
        CashAmount: 0,
        UPIAmount: 0,
        CardAmount: 0,
        BankId: null,
        BalanceAmount: 0,
        Remarks: '',
        // Shipping & Vehicle Details
        DispatchFrom: '',
        DispatchAddress1: '',
        DispatchAddress2: '',
        DispatchLocation: '',
        DispatchPincode: '',
        ShipTo: '',
        ShipGSTNo: '',
        ShipToAddress1: '',
        ShipToAddress2: '',
        ShipToLocation: '',
        ShipToPincode: '',
        VehicleNumber: '',
        TransporterName: '',
        TransporterGST: '',
        Distance: 0,
        IsVerified: false,
        IsActive: true,
        IsOnSite: false,
        IsInstallation: false,
        InstallationDate: '',
        IsInstallationCompleted: false,
        IsCashOnDelivery: false,
        IsExchange: false,
        ExchangeItemName: '',
        ExchangeAmount: 0,
      });
      setSaleDetails([]);
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setSaleDetails([]);
    setSaleBundleDetails([]);
    setSaleStockBranchDetails([]);
    saleOrderIdRef.current = null;
    fetchItems(); // Refresh stock data on cancel/close
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} sale...`);

      const dataToSend = {
        Sale: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || branchId,
          MasterId: parseInt(formData.MasterId) || 0,
          CustomerName: formData.CustomerName || '',
          GSTNumber: formData.GSTNumber || '',
          SalesManId: formData.SalesManId || null,
          StateId: formData.StateId || null,
          InvoiceTypeId: formData.InvoiceTypeId || 0,
          BillNumber: formData.BillNumber || '',
          BillPrefix: formData.BillPrefix || '',
          FinYear: formData.FinYear || '',
          BillDate: formData.BillDate || new Date().toISOString().split('T')[0],
          ClearingDate: formData.ClearingDate || new Date().toISOString().split('T')[0],
          GstCalculationType: formData.GstCalculationType || 2,
          GstCalculationTypeName: formData.GstCalculationTypeName || 'Including Tax',
          Amount: parseFloat(formData.Amount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(formData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(formData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(formData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(formData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(formData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(formData.TCSAmount) || 0,
          RoundOff: parseFloat((parseFloat(formData.RoundOff) || 0).toFixed(2)),
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          GrandTotal: parseFloat(formData.TotalAmount) || 0, // Same as TotalAmount
          CashAmount: parseFloat(formData.CashAmount) || 0,
          UPIAmount: parseFloat(formData.UPIAmount) || 0,
          CardAmount: parseFloat(formData.CardAmount) || 0,
          BankId: formData.BankId || null,
          BalanceAmount: parseFloat(formData.BalanceAmount) || 0,
          Remarks: formData.Remarks || '',
          // Shipping & Vehicle Details
          DispatchFrom: formData.DispatchFrom || '',
          DispatchAddress1: formData.DispatchAddress1 || '',
          DispatchAddress2: formData.DispatchAddress2 || '',
          DispatchLocation: formData.DispatchLocation || '',
          DispatchPincode: formData.DispatchPincode || '',
          ShipTo: formData.ShipTo || '',
          ShipGSTNo: formData.ShipGSTNo || '',
          ShipToAddress1: formData.ShipToAddress1 || '',
          ShipToAddress2: formData.ShipToAddress2 || '',
          ShipToLocation: formData.ShipToLocation || '',
          ShipToPincode: formData.ShipToPincode || '',
          ContactNumber: formData.CustomerContactNumber || '',
          VehicleNumber: formData.VehicleNumber || '',
          TransporterName: formData.TransporterName || '',
          TransporterGST: formData.TransporterGST || '',
          Distance: parseInt(formData.Distance) || 0,
          IsVerified: formData.IsVerified || false,
          IsHold: formData.IsHold || false,
          IsActive: formData.IsActive ?? true,
          IsOnSite: formData.IsOnSite ?? false,
          IsInstallation: formData.IsInstallation ?? false,
          InstallationDate: formData.InstallationDate || null,
          IsCashOnDelivery: formData.IsCashOnDelivery ?? false,
          IsExchange: formData.IsExchange ?? false,
          ExchangeItemName: formData.ExchangeItemName || '',
          ExchangeAmount: parseFloat(formData.ExchangeAmount) || 0,
          SaleOrderId: saleOrderIdRef.current ?? formData.SaleOrderId ?? null,
          EnquiryId: formData.EnquiryId ?? null,
          CreatedBy: modalAction === 'create' ? (formData.SalesManId || userId) : formData.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : formData.CreatedDate,
          ModifiedBy: modalAction === 'edit' ? userId : null,
          ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null
        },
        SaleDetails: saleDetails.map(detail => {
          const selectedItem = items.find(i => i.Id === parseInt(detail.ItemId));
          return {
            Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
            CompanyId: companyId,
            BranchId: formData.BranchId || branchId || 1,
            SaleId: modalAction === 'edit' ? formData.Id : 0,
            ItemId: parseInt(detail.ItemId) || 0,
            ItemName: detail.ItemName || (selectedItem ? (selectedItem.Code ? `${selectedItem.Code} - ${selectedItem.Name}` : selectedItem.Name) : ''),
            HSNCode: detail.HSNCode || selectedItem?.HSNCode || selectedItem?.HSN || '',
            GSTRate: parseFloat(detail.GSTRate) || 0,
            Quantity: parseFloat(detail.Quantity) || 0,
            Rate: parseFloat(detail.Rate) || 0,
            Amount: parseFloat(detail.Amount) || 0,
            DiscountPercentage: parseFloat(detail.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(detail.DiscountAmount) || 0,
            TaxableAmount: parseFloat(detail.TaxableAmount) || 0,
            CGSTAmount: parseFloat(detail.CGSTAmount) || 0,
            SGSTAmount: parseFloat(detail.SGSTAmount) || 0,
            IGSTAmount: parseFloat(detail.IGSTAmount) || 0,
            TotalAmount: parseFloat(detail.TotalAmount) || 0,
            IsActive: detail.IsActive ?? true,
            CreatedBy: modalAction === 'create' ? (formData.SalesManId || userId) : detail.CreatedBy,
            CreatedDate: modalAction === 'create' ? null : detail.CreatedDate,
            ModifiedBy: modalAction === 'edit' ? userId : null,
            ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null,
            PrevQuantity: detail.PrevQuantity || 0,
            BalanceStock: parseFloat(detail.BalanceStock) || 0,
            BatchId: parseInt(detail.BatchId) || 0,
            BatchNumber: detail.BatchNumber || '',
            IsInstallation: detail.IsInstallation ?? false,
          };
        }),
        SaleBundleDetails: saleBundleDetails
          .filter(b => b._parentItemId)
          .map(b => {
            // Find the parent sale detail to get the SaleId/SaleDetailId context
            const parentDetail = saleDetails[b._parentRowIndex];
            return {
              Id: 0,
              SaleId: modalAction === 'edit' ? formData.Id : 0,
              ItemId: parseInt(parentDetail?.ItemId) || 0,         // parent bundle item
              ProductItemId: parseInt(b.ProductItemId) || parseInt(b.ItemId) || null,
              Quantity: parseFloat(b.Quantity) || 0,
              Rate: parseFloat(b.Rate) || 0,
              Amount: parseFloat(b.Amount) || 0,
              IsActive: b.IsActive ?? true,
              CreatedBy: modalAction === 'create' ? userId : b.CreatedBy,
              CreatedDate: modalAction === 'create' ? null : b.CreatedDate,
              ModifiedBy: modalAction === 'edit' ? userId : null,
              ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null,
            };
          }),
        SaleStockBranchDetails: saleDetails.flatMap((detail, index) => {
          // If item has branch splits from the Branch Split modal, use those
          if (detail._branchSplits && detail._branchSplits.length > 0) {
            return detail._branchSplits.map(split => ({
              Id: 0,
              SaleId: modalAction === 'edit' ? formData.Id : 0,
              SaleDetailsId: modalAction === 'edit' ? (detail.Id || 0) : 0,
              BranchId: split.branchId || parseInt(branchId) || null,
              ItemId: parseInt(detail.ItemId) || 0,
              Quantity: split.qty || 0,
              IsActive: detail.IsActive ?? true,
              CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
              CreatedDate: modalAction === 'create' ? null : detail.CreatedDate,
              ModifiedBy: modalAction === 'edit' ? userId : null,
              ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null,
            }));
          }
          // Default: single entry with login branch
          return [{
            Id: modalAction === 'edit' ? (detail.StockBranchDetailId || 0) : 0,
            SaleId: modalAction === 'edit' ? formData.Id : 0,
            SaleDetailsId: modalAction === 'edit' ? (detail.Id || 0) : 0,
            BranchId: parseInt(detail.AssignedBranchId) || parseInt(branchId) || null,
            ItemId: parseInt(detail.ItemId) || 0,
            Quantity: parseFloat(detail.Quantity) || 0,
            IsActive: detail.IsActive ?? true,
            CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
            CreatedDate: modalAction === 'create' ? null : detail.CreatedDate,
            ModifiedBy: modalAction === 'edit' ? userId : null,
            ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null,
          }];
        })
      };

      if (modalAction === 'create') {
        await postData('SaleApi', '', dataToSend, token);
        setSuccess('Sale created successfully!');
        logAudit(AUDIT_ACTION.CREATE, 'SaleApi', 0, null, {
          ...dataToSend.Sale,
          ItemCount: dataToSend.SaleDetails?.length || 0,
        });
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Sale ID is missing');
        await updateData('SaleApi', formData.Id, dataToSend, token);
        setSuccess('Sale updated successfully!');
        // Build old/new from selectedItem (original) vs dataToSend.Sale (saved)
        const oldSale = {
          BillNumber: selectedItem?.BillNumber || '',
          CustomerName: selectedItem?.CustomerName || '',
          GSTNumber: selectedItem?.GSTNumber || '',
          InvoiceTypeName: selectedItem?.InvoiceTypeName || '',
          SalesManId: selectedItem?.SalesManId || null,
          StateId: selectedItem?.StateId || null,
          TaxableAmount: selectedItem?.TaxableAmount || 0,
          TotalAmount: selectedItem?.TotalAmount || 0,
          RoundOff: selectedItem?.RoundOff || 0,
          Remarks: selectedItem?.Remarks || '',
          ItemCount: selectedItem?.ItemCount || 0,
        };
        const newSale = {
          BillNumber: dataToSend.Sale?.BillNumber || '',
          CustomerName: dataToSend.Sale?.CustomerName || '',
          GSTNumber: dataToSend.Sale?.GSTNumber || '',
          InvoiceTypeName: dataToSend.Sale?.InvoiceTypeName || '',
          SalesManId: dataToSend.Sale?.SalesManId || null,
          StateId: dataToSend.Sale?.StateId || null,
          TaxableAmount: dataToSend.Sale?.TaxableAmount || 0,
          TotalAmount: dataToSend.Sale?.TotalAmount || 0,
          RoundOff: dataToSend.Sale?.RoundOff || 0,
          Remarks: dataToSend.Sale?.Remarks || '',
          ItemCount: dataToSend.SaleDetails?.length || 0,
        };
        logAudit(AUDIT_ACTION.UPDATE, 'SaleApi', formData.Id, { ...selectedItem }, { ...formData });
      }

      handleCloseModal();
      await fetchSales();
      await fetchItems(formData.MasterId || 0);
      fetchOrdersCount(); // refresh badge after save
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} sale. Please try again.`);
      setLoading(false);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, billNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(billNo);
    setDeleteModalOpen(true);
  };

  // ── Open Receipt View Modal ─────────────────────────────────────────────
  const handleOpenReceiptModal = async (receiptId) => {
    if (!receiptId) return;
    setReceiptViewLoading(true);
    setReceiptViewModal({ open: true });
    setPaymentPopupAnchor(null);
    setPaymentPopupData(null);
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${receiptId}`, token);
      const freshData = response.response_data || response;
      if (freshData) {
        const p = freshData.PaymentAndReceipt || freshData;
        setReceiptViewFormData({
          ...p,
          BillDate: p.BillDate ? p.BillDate.split('T')[0] : '',
          ChequeDate: p.ChequeDate ? p.ChequeDate.split('T')[0] : '',
          MasterId: p.MasterId || '',
        });
        const purchaseDetails = freshData.PaymentAndReceiptPurchase || [];
        const saleDetails = freshData.PaymentAndReceiptSale || [];
        if (p.IsType && purchaseDetails.length > 0) {
          setReceiptViewDetails(purchaseDetails.map(d => ({
            Id: d.PurchaseId,
            Description: d.PurchaseDescription || d.Description || `Purchase - ${d.PurchaseId}`,
            Amount: parseFloat(d.PurchaseAmount || d.BillAmount || 0),
            PaidAmount: parseFloat(d.PaidAmount || 0),
            PendingAmount: parseFloat(d.PendingAmount || 0),
            BillDate: d.BillDate || p.BillDate,
            Type: 'Purchase',
            PaymentAmount: parseFloat(d.Amount || 0),
            IsSelected: true,
          })));
        } else if (!p.IsType && saleDetails.length > 0) {
          setReceiptViewDetails(saleDetails.map(d => ({
            Id: d.SaleId,
            Description: d.SaleDescription || d.Description || `Sale - ${d.SaleId}`,
            Amount: parseFloat(d.SaleAmount || d.BillAmount || 0),
            PaidAmount: parseFloat(d.PaidAmount || 0),
            PendingAmount: parseFloat(d.PendingAmount || 0),
            BillDate: d.BillDate || p.BillDate,
            Type: 'Sale',
            PaymentAmount: parseFloat(d.Amount || 0),
            IsSelected: true,
          })));
        } else {
          setReceiptViewDetails([]);
        }
      }
    } catch (err) {
      console.error('Error fetching receipt details:', err);
    } finally {
      setReceiptViewLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('SaleApi', deleteItemId, token);
      setSuccess('Sale deleted successfully!');
      logAudit(AUDIT_ACTION.DELETE, 'SaleApi', deleteItemId, { Id: deleteItemId }, null);
      await fetchSales();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete sale.');
    }
    setLoading(false);
  };

  // Activate handlers
  const handleOpenActivateModal = (id, billNo) => {
    setActivateItemId(id);
    setActivateItemNo(billNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  const handleActivate = async () => {
    if (!activateItemId) return;

    setLoading(true);
    handleCloseActivateModal();
    try {
      await activateData('SaleApi', activateItemId, token);
      setSuccess('Sale activated successfully!');
      await fetchSales();
    } catch (error) {
      console.error('Error activating:', error);
      setError('Failed to activate sale.');
    }
    setLoading(false);
  };


  // ─── PDF / Print Bill ────────────────────────────────────────────────────────
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const convertHundreds = (n) => {
      let r = '';
      if (n >= 100) { r += ones[Math.floor(n / 100)] + ' Hundred '; n %= 100; }
      if (n >= 20) { r += tens[Math.floor(n / 10)] + ' '; n %= 10; }
      else if (n >= 10) { r += teens[n - 10] + ' '; return r; }
      if (n > 0) r += ones[n] + ' ';
      return r;
    };
    let result = '';
    const crores = Math.floor(num / 10000000); if (crores > 0) { result += convertHundreds(crores) + 'Crore '; num %= 10000000; }
    const lakhs = Math.floor(num / 100000); if (lakhs > 0) { result += convertHundreds(lakhs) + 'Lakh '; num %= 100000; }
    const thousands = Math.floor(num / 1000); if (thousands > 0) { result += convertHundreds(thousands) + 'Thousand '; num %= 1000; }
    if (num > 0) result += convertHundreds(num);
    return result.trim();
  };

  const generateBillPDF = async (saleId) => {
    logAudit(AUDIT_ACTION.PRINT, 'SaleApi', saleId);
    // ── fetch fresh data from API ──────────────────────────────────────────────
    let freshData;
    try {
      freshData = await fetchSingleSale(saleId);
    } catch (e) {
      console.error('generateBillPDF: fetchSingleSale failed', e);
      throw e;
    }
    if (!freshData) return null;

    const saleData = freshData.Sale || freshData;
    const details = freshData.SaleDetails || [];

    console.log('generateBillPDF: saleData', saleData);
    console.log('generateBillPDF: banks', banks);
    console.log('generateBillPDF: branchData', branchData);
    console.log('generateBillPDF: companyData', companyData);

    // ── colours ───────────────────────────────────────────────────────────────
    const black = [0, 0, 0];
    const white = [255, 255, 255];

    // ── company data ───────────────────────────────────────────────────────────
    const coName = companyData?.Name || 'Company Name';
    const coBranchName = branchData?.BranchName || branchData?.Name || '';
    const coGST = branchData?.GSTNumber || companyData?.GSTNumber || '';
    const coState = branchData?.StateName || companyData?.StateName || 'Kerala';
    const coStateCode = branchData?.StateCode || companyData?.StateCode || '32';
    const coAddr = [branchData?.AddressLine1, branchData?.AddressLine2, branchData?.Location, branchData?.PinCode]
      .filter(Boolean).join(', ') || companyData?.Address || '';
    const coTel1 = branchData?.ContactNumber1 || companyData?.ContactNumber || '';
    const coTel2 = branchData?.ContactNumber2 || companyData?.ContactNumber2 || '';
    const coPhones = [coTel1, coTel2].filter(Boolean).join(',');
    const coGooglePay = branchData?.PaymentQRcodeUPIID || companyData?.PaymentQRcodeUPIID || banks?.[0]?.PaymentQRcodeUPIID || '';

    // Bank details — IsPrimary bank first, fallback to first bank
    const primaryBank = banks?.find(b => b.IsPrimary === true || b.IsPrimary === 1) || banks?.find(b => b.IsActive !== false) || banks?.[0] || {};
    const bankName    = primaryBank.Name          || primaryBank.BankName || '';
    const bankBranch  = primaryBank.BranchName    || '';
    const bankAccount = primaryBank.AccountNumber || primaryBank.AccountNo || '';
    const bankIFSC    = primaryBank.IFSCCode      || primaryBank.IFSC || '';
    const bankGPay    = primaryBank.PaymentQRcodeUPIID || coGooglePay;
    const bankContact = primaryBank.ContactNumber  || primaryBank.ContactNo || primaryBank.Phone || '';

    // ── sale / customer data ────────────────────────────────────────────────────
    const custName = saleData.CustomerName || saleData.ShipTo || '-';
    const custGST = saleData.GSTNumber || saleData.ShipGSTNo || '';
    const custPhone = saleData.CustomerContactNumber || saleData.CustomerPhone || saleData.CustomerMobile || saleData.Mobile || saleData.PhoneNumber || saleData.ContactNumber || saleData.Phone || saleData.Mobile1 || '';
    const custAddress = [
      saleData.ShipToAddress1,
      saleData.ShipToAddress2,
      saleData.ShipToLocation,
      saleData.ShipToPincode
    ].filter(Boolean).join(', ') || saleData.CustomerAddress || saleData.Address || saleData.BillingAddress || '';
    const custState = saleData.StateName || coState;
    const custStateCode = saleData.StateCode || coStateCode;
    const placeOfDelivery = saleData.ShipToLocation || saleData.PlaceOfDelivery || '';
    const billNo = `${saleData.BillPrefix ? saleData.BillPrefix + '/' : ''}${saleData.BillNumber || ''}${saleData.FinYear ? '/' + saleData.FinYear : ''}`;
    const billDate = formatDate(saleData.BillDate);
    const deliveryDate = formatDate(saleData.DeliveryDate || saleData.BillDate);
    const invoiceTypeName = saleData.InvoiceTypeName || 'CREDIT BILL';
    const salesManName = saleData.SalesManName || '';
    const createdByName = saleData.CreatedByName || branchData?.BranchName || '';

    // E-Invoice fields
    const eInvAckNo = saleData.AckNo || saleData.EInvoiceAckNo || '';
    const eInvAckDt = saleData.AckDt || saleData.EInvoiceAckDt || '';
    const eInvIrn = saleData.Irn || saleData.EInvoiceIrn || '';

    // ── pdf setup ─────────────────────────────────────────────────────────────
    const pdf = new jsPDF('p', 'mm', 'a4');
    const PW = pdf.internal.pageSize.width;   // 210
    const PH = pdf.internal.pageSize.height;  // 297
    const M = 5;   // margin
    const CW = PW - 2 * M; // content width = 200

    // helpers
    const drawText = (text, x, y, opts = {}) => {
      const { fs = 8, fw = 'normal', col = black, align = 'left', mw = null } = opts;
      pdf.setFontSize(fs);
      pdf.setFont('helvetica', fw);
      pdf.setTextColor(...col);
      const str = String(text ?? '');
      if (mw) {
        const lines = pdf.splitTextToSize(str, mw);
        pdf.text(lines, x, y, { align });
        return y + lines.length * fs * 0.38;
      }
      pdf.text(str, x, y, { align });
      return y + fs * 0.38;
    };

    const hLine = (x1, y1, x2, y2, lw = 0.3) => {
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(lw);
      pdf.line(x1, y1, x2, y2);
    };

    const drawRect = (x, y, w, h, lw = 0.3) => {
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(lw);
      pdf.rect(x, y, w, h, 'S');
    };

    let y = M;

    // ══════════════════════════════════════════════════════════════════════════
    // OUTER BORDER
    // ══════════════════════════════════════════════════════════════════════════
    drawRect(M, M, CW, PH - 2 * M, 0.5);

    // ══════════════════════════════════════════════════════════════════════════
    // HEADER — Logo + Company Name + Address | Bill No / Date (right side)
    // ══════════════════════════════════════════════════════════════════════════
    const logoSize  = 14;
    const rightColX = M + CW * 0.68; // divider for right info column
    const leftTextX = M + logoSize + 6;
    const leftMaxW  = rightColX - leftTextX - 4;
    const rightValX = rightColX + 22;
    const rightMaxW = M + CW - rightValX - 2;

    // Pre-calculate address lines to size the header dynamically
    pdf.setFontSize(7);
    const addrLines = pdf.splitTextToSize(coAddr, leftMaxW);
    const addrLineCount = addrLines.length;
    const headerH = Math.max(26, 14 + addrLineCount * 3.5 + 8);

    // Logo
    try {
      pdf.addImage(MampillyLogo, 'PNG', M + 3, y + 3, logoSize, logoSize);
    } catch (logoErr) {
      console.warn('generateBillPDF: logo image failed to load, skipping', logoErr);
    }

    // Company Name (bold, large)
    drawText(coName.toUpperCase(), leftTextX, y + 7, { fs: 12, fw: 'bold' });

    // Address line (wrapped)
    let headerTextY = y + 12;
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...black);
    pdf.text(addrLines, leftTextX, headerTextY);
    headerTextY += addrLineCount * 3.5;

    // GST + State
    const gstStateLine = `GST NO : ${coGST}  State : ${coState} - ${coStateCode}`;
    drawText(gstStateLine, leftTextX, headerTextY, { fs: 7 });
    headerTextY += 4;

    // Phone numbers
    if (coPhones) drawText(coPhones, leftTextX, headerTextY, { fs: 7 });

    // Right side: Bill No, Date, Delivery Date, Branch
    hLine(rightColX, y, rightColX, y + headerH);
    let ry = y + 5;
    drawText('Bill No', rightColX + 2, ry, { fs: 7, fw: 'bold' });
    drawText(billNo, rightValX, ry, { fs: 7, mw: rightMaxW });
    ry += 5;
    drawText('Date', rightColX + 2, ry, { fs: 7, fw: 'bold' });
    drawText(billDate, rightValX, ry, { fs: 7, mw: rightMaxW });
    ry += 5;
    drawText('Delivery Date', rightColX + 2, ry, { fs: 7, fw: 'bold' });
    drawText(deliveryDate, rightValX, ry, { fs: 7, mw: rightMaxW });
    ry += 5;
    drawText('Branch', rightColX + 2, ry, { fs: 7, fw: 'bold' });
    drawText(coBranchName || '-', rightValX, ry, { fs: 6.5, mw: rightMaxW });

    y += headerH;
    hLine(M, y, M + CW, y);

    // ══════════════════════════════════════════════════════════════════════════
    // INVOICE TYPE + CUSTOMER SECTION
    // ══════════════════════════════════════════════════════════════════════════
    // Invoice Type (CREDIT BILL / INVOICE) centered
    const invoiceTypeH = 8;
    //drawText(invoiceTypeName.toUpperCase(), M + CW / 2, y + 5, { fs: 9, fw: 'bold', align: 'center' });
    drawText('INVOICE', M + CW / 2, y + 5 + 4, { fs: 9, fw: 'bold', align: 'center' });
    y += invoiceTypeH + 4;
    hLine(M, y, M + CW, y);

    // Customer info row (To / GST No / Ack No / Ack Date)
    const custLeftLines = [custAddress, custPhone].filter(Boolean).length;
    const custRowH = 10 + custLeftLines * 4;
    const midX = M + CW * 0.5;

    drawText('To', M + 2, y + 4, { fs: 7, fw: 'bold' });
    drawText(custName, M + 2, y + 8, { fs: 8, fw: 'bold', mw: midX - M - 5 });
    let custY = y + 12;
    if (custAddress) {
      drawText(custAddress, M + 2, custY, { fs: 7, col: [60, 60, 60], mw: midX - M - 5 });
      custY += 4;
    }
    if (custPhone) {
      drawText(custPhone, M + 2, custY, { fs: 7, col: [30, 64, 175], mw: midX - M - 5 });
    }

    // Right side: GST No, Ack No, Ack Date
    hLine(midX, y, midX, y + custRowH);
    drawText('GST No :', midX + 2, y + 4, { fs: 7, fw: 'bold' });
    drawText(custGST, midX + 22, y + 4, { fs: 7 });
    drawText('Ack No :', midX + 2, y + 8, { fs: 7, fw: 'bold' });
    drawText(eInvAckNo, midX + 22, y + 8, { fs: 7 });
    drawText('Ack Date :', midX + 2, y + 12, { fs: 7, fw: 'bold' });
    drawText(eInvAckDt ? formatDate(eInvAckDt) : '', midX + 22, y + 12, { fs: 7 });

    y += custRowH;
    hLine(M, y, M + CW, y);

    // Phone / Place of Delivery / State / IRN row
    const infoRowH = 8;
    drawText('Phone No :', M + 2, y + 5, { fs: 7, fw: 'bold' });
    drawText(custPhone, M + 24, y + 5, { fs: 7 });

    const pod = M + CW * 0.30;
    drawText('Place Of Delivery :', pod, y + 5, { fs: 7, fw: 'bold' });
    drawText(placeOfDelivery, pod + 30, y + 5, { fs: 7 });

    y += infoRowH;
    hLine(M, y, M + CW, y);

    // State / IRN row
    const stateIrnH = 6;
    drawText(`State    : ${custState}-${custStateCode}`, M + 2, y + 4, { fs: 7 });
    drawText('IRN No :', M + CW * 0.30, y + 4, { fs: 7, fw: 'bold' });
    drawText(eInvIrn, M + CW * 0.30 + 18, y + 4, { fs: 6.5, mw: CW * 0.65 });

    y += stateIrnH;
    hLine(M, y, M + CW, y);

    // ══════════════════════════════════════════════════════════════════════════
    // ITEMS TABLE — matching the traditional invoice format
    // ══════════════════════════════════════════════════════════════════════════
    const hasIGST = details.some(d => (d.IGSTAmount || 0) > 0) || (saleData.IGSTAmount || 0) > 0;
    const hasCSSGST = details.some(d => (d.CGSTAmount || 0) > 0 || (d.SGSTAmount || 0) > 0);

    let tableHead, tableBody;

    if (hasIGST && !hasCSSGST) {
      tableHead = [['No', 'Item', 'HSN/SAC Code', 'Rate', 'Dis %', 'S Rate', 'Qty', 'IGST\n%', 'IGST\nAmt', 'Total']];
      tableBody = details.map((d, i) => {
        const rate = Number(d.Rate || 0);
        const discPct = Number(d.DiscountPercentage || 0);
        const sRate = Number(d.TaxableAmount || 0) / Math.max(Number(d.Quantity || 1), 1);
        return [
          i + 1,
          d.ItemName || items.find(it => it.Id === d.ItemId)?.Name || `Item ${d.ItemId}`,
          d.HSNCode || '',
          rate.toFixed(2),
          discPct > 0 ? discPct.toFixed(2) : '',
          sRate.toFixed(2),
          Number(d.Quantity || 0).toFixed(2),
          d.GSTRate ? `${d.GSTRate}` : '0',
          Number(d.IGSTAmount || 0).toFixed(2),
          Number(d.TotalAmount || 0).toFixed(2),
        ];
      });
    } else {
      tableHead = [[
        { content: 'No', rowSpan: 2 },
        { content: 'Item', rowSpan: 2 },
        { content: 'HSN/SAC Code', rowSpan: 2 },
        { content: 'Rate', rowSpan: 2 },
        { content: 'Dis %', rowSpan: 2 },
        { content: 'S Rate', rowSpan: 2 },
        { content: 'Qty', rowSpan: 2 },
        { content: 'CGST', colSpan: 2 },
        { content: 'SGST', colSpan: 2 },
        { content: 'Total', rowSpan: 2 },
      ], ['%', 'Amt', '%', 'Amt']];
      tableBody = details.map((d, i) => {
        const rate = Number(d.Rate || 0);
        const discPct = Number(d.DiscountPercentage || 0);
        const qty = Math.max(Number(d.Quantity || 1), 1);
        const sRate = Number(d.TaxableAmount || 0) / qty;
        const gstRate = Number(d.GSTRate || 0);
        const halfRate = gstRate / 2;
        return [
          i + 1,
          d.ItemName || items.find(it => it.Id === d.ItemId)?.Name || `Item ${d.ItemId}`,
          d.HSNCode || '',
          rate.toFixed(2),
          discPct > 0 ? discPct.toFixed(2) : '',
          sRate.toFixed(2),
          Number(d.Quantity || 0).toFixed(2),
          halfRate > 0 ? halfRate.toFixed(1) : '0',
          Number(d.CGSTAmount || 0).toFixed(2),
          halfRate > 0 ? halfRate.toFixed(1) : '0',
          Number(d.SGSTAmount || 0).toFixed(2),
          Number(d.TotalAmount || 0).toFixed(2),
        ];
      });
    }

    // Calculate totals for footer row
    const totalQty = details.reduce((sum, d) => sum + Number(d.Quantity || 0), 0);
    const totalCGST = Number(saleData.CGSTAmount || 0);
    const totalSGST = Number(saleData.SGSTAmount || 0);
    const totalIGST = Number(saleData.IGSTAmount || 0);
    const grandTotal = Number(saleData.TotalAmount || saleData.GrandTotal || 0);
    const taxableAmount = Number(saleData.Amount || 0);

    autoTable(pdf, {
      startY: y,
      head: tableHead,
      body: tableBody,
      theme: 'grid',
      headStyles: {
        fillColor: white,
        textColor: black,
        fontSize: 7,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 1.5,
        lineColor: black,
        lineWidth: 0.3,
      },
      bodyStyles: {
        fontSize: 7,
        textColor: black,
        cellPadding: 1.5,
        lineColor: black,
        lineWidth: 0.2,
      },
      columnStyles: hasIGST && !hasCSSGST ? {
        0: { halign: 'center', cellWidth: 8 },
        1: { halign: 'left' },
        2: { halign: 'center', cellWidth: 22 },
        3: { halign: 'right', cellWidth: 18 },
        4: { halign: 'center', cellWidth: 12 },
        5: { halign: 'right', cellWidth: 20 },
        6: { halign: 'right', cellWidth: 12 },
        7: { halign: 'center', cellWidth: 10 },
        8: { halign: 'right', cellWidth: 18 },
        9: { halign: 'right', cellWidth: 22 },
      } : {
        0: { halign: 'center', cellWidth: 8 },
        1: { halign: 'left' },
        2: { halign: 'center', cellWidth: 22 },
        3: { halign: 'right', cellWidth: 17 },
        4: { halign: 'center', cellWidth: 12 },
        5: { halign: 'right', cellWidth: 18 },
        6: { halign: 'right', cellWidth: 12 },
        7: { halign: 'center', cellWidth: 10 },
        8: { halign: 'right', cellWidth: 16 },
        9: { halign: 'center', cellWidth: 10 },
        10: { halign: 'right', cellWidth: 16 },
        11: { halign: 'right', cellWidth: 20 },
      },
      margin: { left: M, right: M },
      tableLineColor: black,
      tableLineWidth: 0.3,
    });

    y = (pdf.lastAutoTable?.finalY || y + 30);

    // ══════════════════════════════════════════════════════════════════════════
    // TOTALS ROW (inline with table bottom)
    // ══════════════════════════════════════════════════════════════════════════
    // Draw a totals summary row
    const totalsRowH = 7;
    hLine(M, y, M + CW, y, 0.3);
    hLine(M, y + totalsRowH, M + CW, y + totalsRowH, 0.3);

    // Get column positions from the table's internal data
    const tblSettings = pdf.lastAutoTable?.settings;
    const tblColumns = pdf.lastAutoTable?.columns;
    
    if (tblColumns && tblColumns.length > 0) {
      // jspdf-autotable columns have 'x' property for position and 'width' for width
      // But the structure varies — let's calculate from columnStyles widths
      if (hasIGST && !hasCSSGST) {
        // IGST layout: No(8), Item(auto), HSN(18), Rate(18), Dis%(12), SRate(20), Qty(12), IGST%(10), IGSTAmt(18), Total(22)
        const fixedWidths = [8, 0, 22, 18, 12, 20, 12, 10, 18, 22]; // 0 = auto
        const fixedTotal = fixedWidths.reduce((s, w) => s + w, 0); // 138
        const autoWidth = CW - fixedTotal; // Item column width
        fixedWidths[1] = autoWidth;
        // Calculate x positions
        let colX = M;
        const colPositions = fixedWidths.map(w => { const x = colX; colX += w; return { x, width: w }; });
        
        const qtyCol = colPositions[6];
        const igstAmtCol = colPositions[8];
        const totalCol = colPositions[9];
        drawText(totalQty.toFixed(0), qtyCol.x + qtyCol.width / 2, y + 5, { fs: 7, fw: 'bold', align: 'center' });
        drawText(totalIGST.toFixed(2), igstAmtCol.x + igstAmtCol.width - 1, y + 5, { fs: 7, fw: 'bold', align: 'right' });
        drawText(grandTotal.toFixed(2), totalCol.x + totalCol.width - 1, y + 5, { fs: 7, fw: 'bold', align: 'right' });
      } else {
        // CGST/SGST layout: No(8), Item(auto), HSN(16), Rate(17), Dis%(12), SRate(18), Qty(12), CGST%(10), CGSTAmt(16), SGST%(10), SGSTAmt(16), Total(20)
        const fixedWidths = [8, 0, 22, 17, 12, 18, 12, 10, 16, 10, 16, 20]; // 0 = auto
        const fixedTotal = fixedWidths.reduce((s, w) => s + w, 0); // 155
        const autoWidth = CW - fixedTotal; // Item column width
        fixedWidths[1] = autoWidth;
        // Calculate x positions
        let colX = M;
        const colPositions = fixedWidths.map(w => { const x = colX; colX += w; return { x, width: w }; });
        
        const qtyCol = colPositions[6];
        const cgstAmtCol = colPositions[8];
        const sgstAmtCol = colPositions[10];
        const totalCol = colPositions[11];
        drawText(totalQty.toFixed(0), qtyCol.x + qtyCol.width / 2, y + 5, { fs: 7, fw: 'bold', align: 'center' });
        drawText(totalCGST.toFixed(2), cgstAmtCol.x + cgstAmtCol.width - 1, y + 5, { fs: 7, fw: 'bold', align: 'right' });
        drawText(totalSGST.toFixed(2), sgstAmtCol.x + sgstAmtCol.width - 1, y + 5, { fs: 7, fw: 'bold', align: 'right' });
        drawText(grandTotal.toFixed(2), totalCol.x + totalCol.width - 1, y + 5, { fs: 7, fw: 'bold', align: 'right' });
      }
    } else {
      // Fallback if table columns not available
      drawText(grandTotal.toFixed(2), M + CW - 3, y + 5, { fs: 7, fw: 'bold', align: 'right' });
    }
    y += totalsRowH;

    // ══════════════════════════════════════════════════════════════════════════
    // BOTTOM SECTION — Bank details (left) | Totals summary (right)
    // ══════════════════════════════════════════════════════════════════════════
    const bottomY = y;
    const rightSummaryX = M + CW * 0.55;
    const rightSummaryW = CW * 0.45;

    // Left side: Bank details
    let ly = bottomY + 5;
    drawText('Bank', M + 3, ly, { fs: 7, fw: 'bold' });
    drawText(`: ${bankName}${bankBranch ? ', ' + bankBranch : ''}`, M + 24, ly, { fs: 7 });
    ly += 4.5;
    drawText('Account No', M + 3, ly, { fs: 7, fw: 'bold' });
    drawText(`: ${bankAccount}`, M + 24, ly, { fs: 7 });
    ly += 4.5;
    drawText('IFSC', M + 3, ly, { fs: 7, fw: 'bold' });
    drawText(`: ${bankIFSC}`, M + 24, ly, { fs: 7 });
    ly += 4.5;
    drawText('GPay No', M + 3, ly, { fs: 7, fw: 'bold' });
    drawText(`: ${bankContact || coPhones || bankGPay || ''}`, M + 24, ly, { fs: 7 });
    ly += 5;

    // Created by info + time
    const billDateTime = saleData.CreatedDate ? new Date(saleData.CreatedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) : '';
    ly += 2;
    drawText(`${createdByName} ${billDateTime}`, M + 3, ly, { fs: 7 });
    ly += 4.5;
    if (salesManName) {
      drawText(`Sales Person : ${salesManName.toUpperCase()}`, M + 3, ly, { fs: 7 });
      ly += 4.5;
    }

    // Right side: Summary totals (draw vertical line after we know where it ends)
    let sy = bottomY + 5;
    const labelX = rightSummaryX + 3;
    const valX = M + CW - 3;

    const summaryLine = (label, value) => {
      drawText(label, labelX, sy, { fs: 7, fw: 'bold' });
      drawText(value, valX, sy, { fs: 7, align: 'right' });
      sy += 5;
    };

    summaryLine('Taxable Amount', taxableAmount.toFixed(2));
    if (hasIGST && !hasCSSGST) {
      summaryLine('IGST', totalIGST.toFixed(2));
    } else {
      summaryLine('CGST', totalCGST.toFixed(2));
      summaryLine('SGST', totalSGST.toFixed(2));
    }
    if ((saleData.DiscountAmount || 0) > 0) {
      summaryLine('Discount', Number(saleData.DiscountAmount).toFixed(2));
    }
    if ((saleData.FreightAmount || 0) > 0) {
      summaryLine('Freight', Number(saleData.FreightAmount).toFixed(2));
    }
    if ((saleData.RoundOff || 0) !== 0) {
      summaryLine('Round Off', Number(saleData.RoundOff).toFixed(2));
    }

    // Separator before grand total
    hLine(rightSummaryX, sy, M + CW, sy);
    sy += 1;
    drawText('Grand Total', labelX, sy + 4, { fs: 9, fw: 'bold' });
    drawText(grandTotal.toFixed(2), valX, sy + 4, { fs: 9, fw: 'bold', align: 'right' });

    // Horizontal line below bank/totals section
    const bottomSectionEndY = Math.max(ly + 2, sy + 8);
    // Draw vertical divider between bank details and totals (stops at section end)
    hLine(rightSummaryX, bottomY, rightSummaryX, bottomSectionEndY);
    hLine(M, bottomSectionEndY, M + CW, bottomSectionEndY);

    // ══════════════════════════════════════════════════════════════════════════
    // AMOUNT IN WORDS
    // ══════════════════════════════════════════════════════════════════════════
    const wordsY = bottomSectionEndY + 5;
    drawText(`Rupees ${numberToWords(Math.floor(grandTotal))} and ${Math.round((grandTotal % 1) * 100)} Paise Only`,
      M + 3, wordsY, { fs: 7, fw: 'bold', mw: rightSummaryX - M - 5 });

    // ══════════════════════════════════════════════════════════════════════════
    // FOOTER — Remarks (left) | Company signature (right)
    // ══════════════════════════════════════════════════════════════════════════
    const footerLineY = wordsY + 6;
    hLine(M, footerLineY, M + CW, footerLineY);

    // Vertical divider in footer section
    const sigLineY = footerLineY + 22;
    hLine(rightSummaryX, footerLineY, rightSummaryX, sigLineY);

    // Left: Remarks / WS code
    if (saleData.Remarks) {
      drawText(saleData.Remarks, M + 3, footerLineY + 5, { fs: 7, mw: rightSummaryX - M - 8 });
    }

    // Right: For Company Name + Authorised Signatory
    drawText(`For  ${coName.toUpperCase()}`, rightSummaryX + 3, footerLineY + 5, { fs: 8, fw: 'bold' });
    drawText('Authorised Signatory', rightSummaryX + 3, footerLineY + 18, { fs: 8, fw: 'bold' });

    // Full horizontal line below Authorised Signatory (bottom border line)
    hLine(M, sigLineY, M + CW, sigLineY, 0.5);

    return { pdf, saleData };
  };

  const handlePrintBill = async (saleId) => {
    setLoading(true);
    setLoadingMessage('Generating PDF...');
    try {
      const result = await generateBillPDF(saleId);
      if (!result) { setError('Failed to fetch sale data.'); setLoading(false); return; }
      const { pdf, saleData } = result;
      pdf.autoPrint();
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) {
        win.onload = () => { win.print(); setTimeout(() => URL.revokeObjectURL(url), 1000); };
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${saleData.BillNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
      setSuccess(`Invoice ${saleData.BillNumber} ready for printing.`);
    } catch (err) {
      console.error('PDF Generation Error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        banks: banks,
        branchData: branchData,
        companyData: companyData
      });
      setError(`Failed to generate PDF: ${err.message || 'Unknown error'}`);
    }
    setLoading(false);
  };

  const handleWhatsApp = async (saleId) => {
    setLoading(true);
    setLoadingMessage('Preparing WhatsApp message...');
    try {
      const result = await generateBillPDF(saleId);
      if (!result) { setError('Failed to fetch sale data.'); setLoading(false); return; }
      const { pdf, saleData } = result;

      const customerName = saleData.CustomerName || customers.find(c => c.Id === saleData.MasterId)?.Name || '';
      const billNo = `${saleData.BillPrefix ? saleData.BillPrefix + '/' : ''}${saleData.BillNumber}`;
      const total = formatCurrency(saleData.TotalAmount || saleData.GrandTotal || 0);
      const companyName = companyData?.Name || 'Us';

      const message = `Hello ${customerName},\n\nThank you for your purchase!\n\nInvoice No: ${billNo}\nDate: ${formatDate(saleData.BillDate)}\nTotal Amount: ${total}\n\nPlease find your invoice attached.\n\nRegards,\n${companyName}`;

      // Try Web Share API with PDF file first
      if (navigator.share && navigator.canShare) {
        const blob = pdf.output('blob');
        const file = new File([blob], `Invoice_${saleData.BillNumber}.pdf`, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `Invoice ${billNo}`, text: message });
          setSuccess('Invoice shared successfully.');
          setLoading(false);
          return;
        }
      }

      // Fallback: download PDF + open WhatsApp web with message
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${saleData.BillNumber}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(waUrl, '_blank');
      setSuccess('PDF downloaded. WhatsApp opened — attach the PDF manually.');
    } catch (err) {
      console.error(err);
      setError('Failed to share via WhatsApp.');
    }
    setLoading(false);
  };
  // ─────────────────────────────────────────────────────────────────────────────

  // Verify handlers
  const handleOpenVerifyModal = async (item) => {
    setModalLoading(true);
    const freshData = await fetchSingleSale(item.Id);

    if (freshData) {
      const saleData = freshData.Sale || freshData;
      const details = freshData.SaleDetails || [];

      setVerifySaleData(saleData);
      setVerifySaleDetails(details);
    } else {
      setVerifySaleData(item);
      setVerifySaleDetails([]);
    }
    setModalLoading(false);
    setVerifyModalOpen(true);
  };

  const handleCloseVerifyModal = () => {
    setVerifyModalOpen(false);
    setVerifySaleData(null);
    setVerifySaleDetails([]);
  };

  const handleVerify = async (saleId, updatedDetails) => {
    setLoading(true);
    setLoadingMessage('Updating sale...');

    try {
      const dataToSend = {
        Sale: {
          ...verifySaleData,
          ModifiedBy: userId
        },
        SaleDetails: updatedDetails.map(d => ({
          ...d,
          ModifiedBy: userId
        })),
        SaleStockBranchDetails: updatedDetails.flatMap(d => {
          if (d._branchSplits && d._branchSplits.length > 0) {
            return d._branchSplits.map(split => ({
              Id: 0,
              SaleId: saleId,
              SaleDetailsId: d.Id || 0,
              BranchId: split.branchId || parseInt(branchId) || null,
              ItemId: parseInt(d.ItemId) || 0,
              Quantity: split.qty || 0,
              IsActive: d.IsActive ?? true,
              CreatedBy: d.CreatedBy || null,
              CreatedDate: d.CreatedDate || null,
              ModifiedBy: userId,
              ModifiedDate: new Date().toISOString(),
            }));
          }
          return [{
            Id: d.StockBranchDetailId || 0,
            SaleId: saleId,
            SaleDetailsId: d.Id || 0,
            BranchId: parseInt(d.AssignedBranchId) || parseInt(branchId) || null,
            ItemId: parseInt(d.ItemId) || 0,
            Quantity: parseFloat(d.Quantity) || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy || null,
            CreatedDate: d.CreatedDate || null,
            ModifiedBy: userId,
            ModifiedDate: new Date().toISOString(),
          }];
        })
      };

      await updateData('SaleApi', saleId, dataToSend, token);
      setSuccess('Sale updated successfully!');
        logAudit(AUDIT_ACTION.UPDATE, 'SaleApi', formData.Id, { ...selectedItem }, { ...formData });
      await fetchSales();
    } catch (error) {
      console.error('Error updating sale:', error);
      setError('Failed to update sale.');
    }
    setLoading(false);
  };

  const handleActualVerify = async (saleId, updatedDetails) => {
    setLoading(true);
    setLoadingMessage('Verifying sale...');

    try {
      await VerifyData('SaleApi', saleId, token);
      setSuccess('Sale verified successfully!');
      logAudit(AUDIT_ACTION.VERIFY, 'SaleApi', saleId);
      await fetchSales();
      handleCloseVerifyModal();
    } catch (error) {
      console.error('Error verifying sale:', error);
      setError('Failed to verify sale.');
    }
    setLoading(false);
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    if (!Array.isArray(data)) return [];

    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        String(item.BillNumber ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customers.find(c => c.MasterId === item.MasterId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleRefresh = () => fetchAllData();
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  // Orders modal — fetch confirmed enquiries (IsConfirmOrder: true)
  const handleOpenOrdersModal = async () => {
    setOrdersModalOpen(true);
    setOrdersLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const from = '2000-01-01';
      const res = await fetchData('EnquiryApi', `/${companyId}/${branchId}/0/${from}/${today}`, token);
      const all = res?.response_data || res || [];
      const filtered = Array.isArray(all) ? all.filter(e => e.IsConfirmOrder === true && e.IsSaled === false) : [];
      setConfirmedOrders(filtered);
      setOrdersCount(filtered.length);
    } catch (e) {
      setConfirmedOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Convert confirmed enquiry → Sale (autopopulate)
  const handleConvertToSale = async (enquiry) => {
    setOrdersModalOpen(false);
    setModalAction('create');
    setModalLoading(true);

    try {
      const gstData = await fetchFreightAndPackingGST();
      // Fetch items directly to get fresh list for ItemName lookup
      let freshItems = items;
      try {
        const itemsRes = await fetchData('SaleApi', `/ItemStockListDetails/ItemStockListDetails/${companyId}/${branchId}/${branchId}/${enquiry.MasterId || 0}`, token);
        freshItems = itemsRes.response_data || itemsRes || [];
        setItems(freshItems);
      } catch (e) { /* use existing items */ }

      // Fetch full enquiry details
      const res = await fetchData('EnquiryApi', `/${enquiry.Id}`, token);
      const freshData = res?.response_data || res;
      const enquiryData = freshData?.Enquiry || freshData;
      const enquiryDetails = freshData?.EnquiryDetails || freshData?.Details || [];

      // Fetch customer contact & address from API
      const masterId = enquiryData.MasterId || enquiry.MasterId;
      let customerContactNumber = enquiryData.ContactNumber || enquiry.ContactNumber || '';
      let shipAddress1 = '';
      let shipAddress2 = '';
      let shipLocation = '';
      let shipPincode = '';
      let shipGSTNo = '';
      if (masterId) {
        try {
          const clientRes = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${masterId}`, token);
          const clientData = clientRes?.response_data?.Ledger || clientRes?.response_data || clientRes || {};
          if (clientData) {
            customerContactNumber = customerContactNumber || clientData.ContactNumber1 || clientData.ContactNumber || clientData.Mobile || '';
            shipAddress1 = clientData.AddressLine1 || '';
            shipAddress2 = clientData.AddressLine2 || '';
            shipLocation = clientData.Location || '';
            shipPincode  = clientData.PinCode || '';
            shipGSTNo    = clientData.GSTNumber || '';
          }
        } catch (e) {
          console.warn('Order→Sale: could not fetch customer details', e);
        }
      }
      if (!shipAddress1 && enquiryData.Address) {
        shipAddress1 = enquiryData.Address;
      }

      // Fetch item names for all unique ItemIds from enquiry
      const uniqueItemIds = [...new Set(enquiryDetails.map(d => d.ItemId).filter(Boolean))];
      const itemNameMap = {};
      await Promise.allSettled(
        uniqueItemIds.map(async (itemId) => {
          try {
            const r = await fetchData('ItemApi', `/Item/${itemId}`, token);
            const iData = r?.response_data || r;
            const iObj = iData?.Item || iData;
            if (iObj?.Name) itemNameMap[itemId] = iObj.Code ? `${iObj.Code} - ${iObj.Name}` : iObj.Name;
          } catch (e) { /* skip */ }
        })
      );

      // Resolve InvoiceType — use enquiry's InvoiceTypeId, fallback to B To C
      const invoiceTypesRes = await fetchData('InvoiceTypeApi', `/${companyId}`, token);
      const allInvoiceTypes = invoiceTypesRes.response_data || invoiceTypesRes || [];
      const enquiryInvoiceTypeId = enquiryData.InvoiceTypeId || enquiry.InvoiceTypeId || 0;
      let resolvedInvoiceType = enquiryInvoiceTypeId
        ? allInvoiceTypes.find(it => it.Id === parseInt(enquiryInvoiceTypeId))
        : null;
      if (!resolvedInvoiceType) {
        resolvedInvoiceType = allInvoiceTypes.find(it =>
          it.Name.toLowerCase().includes('b to c') || it.Name.toLowerCase().includes('b2c')
        );
      }

      // Resolve SalesManId/SalesManName from enquiry ([NotMapped] fields from API)
      const enquirySalesManId = enquiryData.SalesmanId || enquiryData.SalesManId || enquiry.SalesmanId || null;
      const enquirySalesManName = enquiryData.SalesmanName || enquiryData.SalesManName || enquiry.SalesmanName || '';

      // Base create formData + customer from enquiry
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        MasterId: enquiryData.MasterId || '',
        CustomerName: enquiryData.CustomerName || '',
        SalesManId: enquirySalesManId,
        SalesManName: enquirySalesManName,
        InvoiceTypeId: resolvedInvoiceType?.Id || 0,
        InvoiceTypeName: resolvedInvoiceType?.Name || 'B to C',
        BillNumber: '',
        BillPrefix: '',
        FinYear: '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        GstCalculationType: 2,
        GstCalculationTypeName: 'Including Tax',
        Amount: 0, DiscountAmount: 0, TaxableAmount: 0,
        CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0,
        FreightAmount: 0, FreightGSTRate: parseFloat(gstData?.freight?.GSTRate) || 0, FreightGSTAmount: 0,
        PackingAmount: 0, PackingGSTRate: parseFloat(gstData?.packing?.GSTRate) || 0, PackingGSTAmount: 0,
        TCSAmount: 0, RoundOff: 0, TotalAmount: 0, GrandTotal: 0,
        CashAmount: 0, UPIAmount: 0, CardAmount: 0, BankId: null, BalanceAmount: 0,
        Remarks: '',
        DispatchFrom: branchData?.BranchName || companyData?.CompanyName || '',
        DispatchAddress1: branchData?.AddressLine1 || companyData?.AddressLine1 || '',
        DispatchAddress2: branchData?.AddressLine2 || companyData?.AddressLine2 || '',
        DispatchLocation: branchData?.Location || companyData?.Location || '',
        DispatchPincode: branchData?.Pincode || companyData?.Pincode || '',
        ShipTo: enquiryData.CustomerName || '', ShipGSTNo: shipGSTNo, ShipToAddress1: shipAddress1, ShipToAddress2: shipAddress2, ShipToLocation: shipLocation, ShipToPincode: shipPincode,
        CustomerContactNumber: customerContactNumber,
        VehicleNumber: '', TransporterName: branchData?.TransporterName || companyData?.TransporterName || '', TransporterGST: branchData?.GSTNumber || companyData?.GSTNumber || '', Distance: 0,
        IsVerified: false, IsActive: true,
        EnquiryId: enquiry.Id
      });
      // Open modal AFTER formData is set so all fields are populated
      setOpenModal(true);

      // Map enquiry details → sale details using freshly loaded items
      setSaleDetails(enquiryDetails.map((d, idx) => {
          const itemObj = freshItems.find(i => i.Id === parseInt(d.ItemId));
          const resolvedName = d.ItemName || itemNameMap[d.ItemId] || itemObj?.Name || '';
          return {
            Id: 0,
            ItemId: d.ItemId,
            ItemName: resolvedName,
            _rowKey: `row_${Date.now()}_${idx}`,
            _rowIndex: idx,
            AssignedBranchId: parseInt(branchId) || null,
            HSNCode: d.HSNCode || itemObj?.HSNCode || '',
            GSTRate: d.GSTRate || 0,
            Quantity: d.Quantity || 0,
            Rate: d.Rate || 0,
            MinRate: itemObj?.MinRate || 0,
            MaxRate: itemObj?.MaxRate || 0,
            BalanceStock: parseFloat(itemObj?.CurrentBranchStock ?? itemObj?.BalanceStock) || 0,
            Amount: d.Amount || 0,
            DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0,
            TaxableAmount: d.TaxableAmount || 0,
            CGSTAmount: d.CGSTAmount || 0,
            SGSTAmount: d.SGSTAmount || 0,
            IGSTAmount: d.IGSTAmount || 0,
            TotalAmount: d.TotalAmount || 0,
            BatchNumber: '', BatchId: 0,
            HasWarranty: false,
            BilledQuantity: d.Quantity || 0,
            OrderQuantity: d.Quantity || 0,
            StockQuantity: 0, DamageQuantity: 0, ExcessQuantity: 0,
            LessQuantity: 0, ReturnDamageQuantity: 0, ReturnStockQuantity: 0,
            IsActive: true
          };
        }));
      setSaleBundleDetails([]);
      setSaleStockBranchDetails([]);
    } catch (e) {
      console.error('Error converting enquiry to sale:', e);
      setSaleDetails([]);
      setOpenModal(true); // Still open modal even if fetch failed
    } finally {
      setModalLoading(false);
    }
  };

  // Fetch orders count on load
  const fetchOrdersCount = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetchData('EnquiryApi', `/${companyId}/${branchId}/0/2000-01-01/${today}`, token);
      const all = res?.response_data || res || [];
      setOrdersCount(Array.isArray(all) ? all.filter(e => e.IsConfirmOrder === true && e.IsSaled === false).length : 0);
    } catch (e) { setOrdersCount(0); }
  };

  // Date change handlers
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(sales);

  // SO TO SALES

  // Converted SO List modal states
  const [convertedSOModalOpen, setConvertedSOModalOpen] = useState(false);
  const [convertedSOList, setConvertedSOList] = useState([]);
  const [requestCount, setRequestCount] = useState(0);

  // Fetch Converted SO List
  const fetchConvertedSOList = async () => {
    try {
      const response = await fetchData('SaleApi', `/SaleOrderToSaleList/${companyId}/${branchId}`, token);
      return response.response_data || response || [];
    } catch (error) {
      console.error('Error fetching converted SO list:', error);
      return [];
    }
  };

  // Handle Request button click - Open Converted PO List modal
  const handleOpenConvertedSOModal = async () => {
    setModalLoading(true);
    const soList = await fetchConvertedSOList();
    setConvertedSOList(soList);
    setRequestCount(soList.length); // Set the count
    setModalLoading(false);
    setConvertedSOModalOpen(true);
  };

  const handleCloseConvertedSOModal = () => {
    setConvertedSOModalOpen(false);
    setConvertedSOList([]);
  };

  // SALE ORDER TO SALE 
  // SO to Sale Create modal states
  const [soToSaleModalOpen, setSOToSaleModalOpen] = useState(false);
  const [selectedSOData, setSelectedSOData] = useState(null);
  const [selectedSODetails, setSelectedSODetails] = useState([]);
  const [soToSaleFormData, setSOToSaleFormData] = useState({});

  // Fetch SO details for SO to SALE
  const fetchSODetailsForSale = async (soId) => {
    try {
      const response = await fetchData('SaleOrderApi', `/${soId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching SO details:', error);
      return null;
    }
  };

  useEffect(() => {
    if (selectedSODetails.length > 0) {
      calculateSOTotals(selectedSODetails);
    }
  }, [selectedSODetails.length]); // Only trigger when items are added/removed

  // Calculate PO to Purchase totals
  const calculateSOTotals = (details) => {
    console.log('Calculating SO totals for details:', details);

    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    console.log('Calculated item totals:', totals);

    // Get current freight and packing amounts from form data
    const freightAmount = parseFloat(soToSaleFormData.FreightAmount) || 0;
    const packingAmount = parseFloat(soToSaleFormData.PackingAmount) || 0;

    // Get GST rates - use default rates from freight/packing GST data or highest item GST rate
    const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
    const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
    const maxItemGSTRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.GSTRate) || 0)) : 0;

    const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
    const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

    // Calculate GST amounts
    const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
    const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

    const tcsAmount = parseFloat(soToSaleFormData.TCSAmount) || 0;

    // Calculate grand total
    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
    const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
    const finalTotal = Math.round(grandTotal);

    const paidAmount =
      (parseFloat(soToSaleFormData.CardAmount) || 0) +
      (parseFloat(soToSaleFormData.CashAmount) || 0) +
      (parseFloat(soToSaleFormData.UPIAmount) || 0);
    const safeBalance = Math.max(0, finalTotal - paidAmount);
    const creditAmount = safeBalance;

    // Update form data with calculated totals
    setSOToSaleFormData(prev => {
      const updatedFormData = {
        ...prev,
        Amount: Math.round(totals.Amount * 100) / 100,
        DiscountAmount: Math.round(totals.DiscountAmount * 100) / 100,
        TaxableAmount: Math.round(totals.TaxableAmount * 100) / 100,
        CGSTAmount: Math.round(totals.CGSTAmount * 100) / 100,
        SGSTAmount: Math.round(totals.SGSTAmount * 100) / 100,
        IGSTAmount: Math.round(totals.IGSTAmount * 100) / 100,
        FreightGSTRate: effectiveFreightGSTRate,
        FreightGSTAmount: Math.round(freightGSTAmount * 100) / 100,
        PackingGSTRate: effectivePackingGSTRate,
        PackingGSTAmount: Math.round(packingGSTAmount * 100) / 100,
        RoundOff: Math.round(roundOff * 100) / 100,
        TotalAmount: finalTotal,
        CreditAmount: creditAmount,
        BalanceAmount: safeBalance
      };

      console.log('Updated form data:', updatedFormData);
      return updatedFormData;
    });
  };

  // Handle "To SALE" button click — fetch SO data and open SaleModal pre-filled
  const handleOpenSOToSaleModal = async (so) => {
    setModalLoading(true);
    setConvertedSOModalOpen(false);

    const soDetails = await fetchSODetailsForSale(so.Id);

    if (soDetails) {
      const soData = soDetails.SaleOrder || soDetails;
      const details = soDetails.SaleOrderDetails || [];

      setSelectedSOData(soData);
      setSelectedSODetails(details);

      // Resolve customer from customers list
      const customer = customers.find(c => (c.MasterId || c.Id) === soData.MasterId);
      const customerName = soData.CustomerName || customer?.Name || '';
      const gstNumber = customer?.GSTNumber || customer?.GSTNo || '';
      // Default InvoiceType to B To C for SO→Sale conversion (user can change if needed)
      const invoiceTypeName = customer?.InvoiceTypeName || customer?.InvoiceType || '';
      const invoiceTypeId = soData.InvoiceTypeId || customer?.InvoiceTypeId || 0;

      // Fetch invoice types to get B To C as default
      let defaultInvoiceTypeId = invoiceTypeId;
      let defaultInvoiceTypeName = invoiceTypeName;
      try {
        const invoiceTypesRes = await fetchData('InvoiceTypeApi', `/${companyId}`, token);
        const allInvoiceTypes = invoiceTypesRes.response_data || invoiceTypesRes || [];
        const b2cType = allInvoiceTypes.find(it =>
          it.Name.toLowerCase().includes('b to c') || it.Name.toLowerCase().includes('b2c')
        );
        if (b2cType) {
          defaultInvoiceTypeId = b2cType.Id;
          defaultInvoiceTypeName = b2cType.Name;
        }
      } catch (e) { /* fallback to customer's invoice type */ }

      // Fetch customer contact & address from API
      let customerContactNumber = customer?.ContactNumber1 || customer?.ContactNumber || '';
      let shipAddress1 = customer?.AddressLine1 || '';
      let shipAddress2 = customer?.AddressLine2 || '';
      let shipLocation = customer?.Location || '';
      let shipPincode  = customer?.PinCode || '';
      try {
        const clientRes = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${soData.MasterId}`, token);
        const clientData = clientRes?.response_data?.Ledger || clientRes?.response_data || clientRes || {};
        if (clientData) {
          customerContactNumber = clientData.ContactNumber1 || clientData.ContactNumber || customerContactNumber;
          shipAddress1 = clientData.AddressLine1 || shipAddress1;
          shipAddress2 = clientData.AddressLine2 || shipAddress2;
          shipLocation = clientData.Location  || shipLocation;
          shipPincode  = clientData.PinCode   || shipPincode;
        }
      } catch (e) {
        console.warn('SO→Sale: could not fetch customer details', e);
      }

      // Fetch Sale bill number based on B To C invoice type
      const invoiceTypeParam = defaultInvoiceTypeId ? `/${defaultInvoiceTypeId}` : '';
      let billNumber = '', billPrefix = '', finYear = '';
      try {
        const billRes = await fetchData('SaleApi', `/MaxBillno/${companyId}/${branchId || 1}${invoiceTypeParam}`, token);
        const billData = billRes.response_data || billRes || {};
        billNumber = billData.BillNumber ?? billData.billNumber ?? '';
        billPrefix = billData.BillPrefix || billData.billPrefix || '';
        finYear = billData.FinYear || billData.finYear || '';
      } catch (e) {
        console.error('Error fetching bill number:', e);
      }

      // Totals from SO
      const freightAmount = soData.FreightAmount || 0;
      const packingAmount = soData.PackingAmount || 0;
      const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
      const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
      const maxItemGSTRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.GSTRate) || 0)) : 0;
      const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
      const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);
      const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
      const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;
      const itemsTotal = details.reduce((s, d) => s + (parseFloat(d.TotalAmount) || 0), 0);
      const grandTotal = itemsTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount;
      const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
      const finalTotal = Math.round(grandTotal);

      // Pre-fill SaleModal formData from SO
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        MasterId: soData.MasterId || '',
        CustomerName: customerName,
        GSTNumber: gstNumber,
        InvoiceTypeId: defaultInvoiceTypeId,
        InvoiceTypeName: defaultInvoiceTypeName,
        BillNumber: billNumber,
        BillPrefix: billPrefix,
        FinYear: finYear,
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        GstCalculationType: soData.GstCalculationType || 2,
        GstCalculationTypeName: soData.GstCalculationTypeName || 'Including Tax',
        Amount: details.reduce((s, d) => s + (parseFloat(d.Amount) || 0), 0),
        DiscountAmount: details.reduce((s, d) => s + (parseFloat(d.DiscountAmount) || 0), 0),
        TaxableAmount: details.reduce((s, d) => s + (parseFloat(d.TaxableAmount) || 0), 0),
        CGSTAmount: details.reduce((s, d) => s + (parseFloat(d.CGSTAmount) || 0), 0),
        SGSTAmount: details.reduce((s, d) => s + (parseFloat(d.SGSTAmount) || 0), 0),
        IGSTAmount: details.reduce((s, d) => s + (parseFloat(d.IGSTAmount) || 0), 0),
        FreightAmount: freightAmount,
        FreightGSTRate: effectiveFreightGSTRate,
        FreightGSTAmount: freightGSTAmount,
        PackingAmount: packingAmount,
        PackingGSTRate: effectivePackingGSTRate,
        PackingGSTAmount: packingGSTAmount,
        TCSAmount: 0,
        RoundOff: roundOff,
        TotalAmount: finalTotal,
        GrandTotal: finalTotal,
        CashAmount: 0,
        UPIAmount: 0,
        CardAmount: 0,
        BankId: null,
        BalanceAmount: finalTotal,
        Remarks: soData.Remarks || '',
        DispatchFrom: '', DispatchAddress1: '', DispatchAddress2: '',
        DispatchLocation: '', DispatchPincode: '',
        ShipTo: customerName, ShipGSTNo: gstNumber,
        ShipToAddress1: shipAddress1,
        ShipToAddress2: shipAddress2,
        ShipToLocation: shipLocation,
        ShipToPincode: shipPincode,
        CustomerContactNumber: customerContactNumber,
        VehicleNumber: '', TransporterName: '', TransporterGST: '', Distance: 0,
        IsVerified: false, IsActive: true,
        SaleOrderId: soData.Id,
      });

      // Store SaleOrderId in ref so it's never lost by async state updates
      saleOrderIdRef.current = soData.Id;

      // Fetch items for this customer's master ID to ensure item names resolve correctly
      let freshItems = items;
      try {
        const itemsRes = await fetchData('SaleApi', `/ItemStockListDetails/ItemStockListDetails/${companyId}/${branchId}/${branchId}/${soData.MasterId || 0}`, token);
        freshItems = itemsRes.response_data || itemsRes || items;
        setItems(freshItems);
      } catch (e) {
        console.error('Error fetching items for SO:', e);
      }

      // Pre-fill sale details from SO details — include all fields needed for branch split, batches, etc.
      setSaleDetails(details.map((d, idx) => {
        const freshItem = freshItems.find(i => i.Id === parseInt(d.ItemId));
        return {
          Id: 0,
          _rowKey: `row_${Date.now()}_${Math.random()}_${idx}`,  // required for Branch Split Modal
          _rowIndex: idx,
          ItemId: d.ItemId,
          ItemName: d.ItemName || freshItem?.Name || '',
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          MinRate: d.MinRate || freshItem?.MinRate || 0,
          MaxRate: d.MaxRate || freshItem?.MaxRate || 0,
          HasWarranty: d.HasWarranty || freshItem?.HasWarranty || false,
          IsInstallation: d.IsInstallation ?? false,
          BalanceStock: parseFloat(freshItem?.CurrentBranchStock ?? freshItem?.BalanceStock) || 0,
          AssignedBranchId: parseInt(branchId) || null,  // default to login branch
          BatchNumber: '',
          BatchId: 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          BilledQuantity: d.Quantity || 0,
          OrderQuantity: d.Quantity || 0,
          StockQuantity: d.Quantity || 0,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          IsActive: true,
        };
      }));

      setModalAction('create');
      setOpenModal(true);
    }

    setModalLoading(false);
  };

  const handleCloseSOToSaleModal = () => {
    setSOToSaleModalOpen(false);
    setSelectedSOData(null);
    setSelectedSODetails([]);
    setSOToSaleFormData({});
  };

  // To open sale from SO 
  // Handle PO Details View Modal
  // PO Details View modal states
  const [soDetailsModalOpen, setSODetailsModalOpen] = useState(false);
  const [soDetailsData, setSODetailsData] = useState(null);
  const [soDetailsItems, setSODetailsItems] = useState([]);


  const handleOpenSODetailsModal = async (soData) => {
    setModalLoading(true);

    try {
      const soDetails = await fetchSODetailsForSale(soData.Id);

      if (soDetails) {
        const soInfo = soDetails.SaleOrder || soDetails;
        const details = soDetails.SaleOrderDetails || [];

        setSODetailsData(soInfo);
        setSODetailsItems(details);
      } else {
        setSODetailsData(soData);
        setSODetailsItems([]);
      }
    } catch (error) {
      console.error('Error fetching SO details:', error);
      setSODetailsData(soData);
      setSODetailsItems([]);
    }

    setModalLoading(false);
    setSODetailsModalOpen(true);
  };

  const handleCloseSODetailsModal = () => {
    setSODetailsModalOpen(false);
    setSODetailsData(null);
    setSODetailsItems([]);
  };



  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <LogoLoader open={loading || modalLoading} message={loadingMessage} />

      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Sale Management</h4>
        </div>

        {/* Search, Filters and Create */}
        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, overflowY: 'visible' }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" lang="en-GB" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" lang="en-GB" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
              </select>
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="SO Requests" arrow>
                <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                  <IconButton onClick={handleOpenConvertedSOModal} size="small" sx={{ background: '#8b5cf6', color: 'white', borderRadius: '8px', height: '32px', width: '32px', '&:hover': { background: '#7c3aed' } }}>
                    <Assignment sx={{ fontSize: '16px' }} />
                  </IconButton>
                  {requestCount > 0 && (
                    <Box sx={{ position: 'absolute', bottom: -6, right: -6, backgroundColor: '#ef4444', color: 'white', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold', border: '2px solid white', zIndex: 1 }}>
                      {requestCount > 99 ? '99+' : requestCount}
                    </Box>
                  )}
                </Box>
              </Tooltip>
              <Tooltip title="Orders" arrow>
                <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                  <IconButton onClick={handleOpenOrdersModal} size="small" sx={{ background: '#d97706', color: 'white', borderRadius: '8px', height: '32px', width: '32px', '&:hover': { background: '#b45309' } }}>
                    <ShoppingCart sx={{ fontSize: '16px' }} />
                  </IconButton>
                  {ordersCount > 0 && (
                    <Box sx={{ position: 'absolute', bottom: -6, right: -6, backgroundColor: '#dc2626', color: 'white', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold', border: '2px solid white', zIndex: 1 }}>
                      {ordersCount > 99 ? '99+' : ordersCount}
                    </Box>
                  )}
                </Box>
              </Tooltip>
              <Tooltip title="Add Sale" arrow>
                <IconButton onClick={() => handleOpenModal('create')} disabled={!hasCreateMasterPermission} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                  <Add sx={{ fontSize: '18px' }} />
                </IconButton>
              </Tooltip>
            </Box>
            {showSearch && (              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by Bill No, Customer..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by Bill No, Customer..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <input type="date" lang="en-GB" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <input type="date" lang="en-GB" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px' }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="SO Requests" arrow>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Button variant="contained" onClick={handleOpenConvertedSOModal} sx={{ background: '#8b5cf6', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { background: '#7c3aed' } }}>
                  <Assignment sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Request</span>
                </Button>
                {requestCount > 0 && (
                  <Box sx={{ position: 'absolute', top: -6, right: -6, backgroundColor: '#ef4444', color: 'white', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold', border: '2px solid white' }}>
                    {requestCount > 99 ? '99+' : requestCount}
                  </Box>
                )}
              </Box>
            </Tooltip>
            <Tooltip title="Orders" arrow>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Button variant="contained" onClick={handleOpenOrdersModal}
                  sx={{ background: '#d97706', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { background: '#b45309' } }}>
                  <ShoppingCart sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Orders</span>
                </Button>
                {ordersCount > 0 && (
                  <Box sx={{ position: 'absolute', top: -6, right: -6, backgroundColor: '#dc2626', color: 'white', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold', border: '2px solid white', zIndex: 1 }}>
                    {ordersCount > 99 ? '99+' : ordersCount}
                  </Box>
                )}
              </Box>
            </Tooltip>
            <Tooltip title="Add Sale" arrow>
              <Button variant="contained" onClick={() => handleOpenModal('create')} disabled={!hasCreateMasterPermission}
                sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Add Sale</span>
              </Button>
            </Tooltip>
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Bill No.', 'Date', 'Customer', 'Total', 'Cash', 'Card', 'UPI', 'Balance', 'Pmt Status', 'Actions'].map((h, i) => (
                    <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i >= 4 && i <= 8 ? 'right' : i === 10 ? 'right' : 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(paginatedData) && paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.Id || index} style={{ background: item.IsHold ? '#fee2e2' : index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: item.IsHold ? '2px solid #f87171' : '2px solid #f0f0f0', borderLeft: item.IsHold ? '4px solid #dc2626' : 'none' }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>{startIndex + index + 1}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <ShoppingCart sx={{ fontSize: 16, color: '#2C3E6E' }} />
                          {item.BillPrefix ? `${item.BillPrefix}-` : ''}{item.BillNumber || '-'}{item.FinYear ? `/${item.FinYear}` : ''}
                          {item.IsInstallation && (
                            <Chip
                              label="🔧 Installation"
                              size="small"
                              sx={{
                                fontSize: '0.6rem', height: '18px', fontWeight: 700,
                                bgcolor: '#ede9fe', color: '#7c3aed',
                                border: '1px solid #c4b5fd',
                                '& .MuiChip-label': { px: 0.8 }
                              }}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.BillDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.CustomerName || customers.find(c => c.MasterId === item.MasterId)?.Name || '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 700, textAlign: 'right' }}>{formatCurrency(item.TotalAmount)}</TableCell>
                      {/* <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#059669', fontWeight: 600, textAlign: 'right' }}>{formatCurrency(item.BalanceAmount)}</TableCell> */}
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#059669', fontWeight: 600, textAlign: 'right' }}>{formatCurrency(item.CashAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#1e40af', fontWeight: 600, textAlign: 'right' }}>{formatCurrency(item.CardAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#7c3aed', fontWeight: 600, textAlign: 'right' }}>{formatCurrency(item.UPIAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#dc2626', fontWeight: 600, textAlign: 'right' }}>{formatCurrency(item.BalanceAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'center' }}>
                        <Chip
                          label={item.PaymentStatus || '-'}
                          size="small"
                          onClick={(e) => {
                            setPaymentPopupAnchor(e.currentTarget);
                            setPaymentPopupData(item);
                          }}
                          sx={{
                            fontSize: '0.65rem',
                            height: '22px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            bgcolor: item.PaymentStatus === 'Paid' ? '#d1fae5' : item.PaymentStatus === 'Partial' ? '#fef3c7' : '#fee2e2',
                            color: item.PaymentStatus === 'Paid' ? '#065f46' : item.PaymentStatus === 'Partial' ? '#92400e' : '#991b1b',
                            '&:hover': { opacity: 0.8 }
                          }}
                        />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <Tooltip title="View" arrow>
                            <span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handleOpenModal('view', item.Id)}>👁️</span>
                          </Tooltip>
                          <Tooltip title="Print Bill" arrow>
                            <span style={{ cursor: item.IsHold ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: item.IsHold ? 0.3 : 1 }} onClick={() => !item.IsHold && handlePrintBill(item.Id)}>🖨️</span>
                          </Tooltip>
                          <Tooltip title={item.IsHold ? 'Sale is on Hold' : 'Send via WhatsApp'} arrow>
                            <span style={{ cursor: item.IsHold ? 'not-allowed' : 'pointer', fontSize: '14px', color: item.IsHold ? '#9ca3af' : '#25D366', display: 'inline-flex', alignItems: 'center', opacity: item.IsHold ? 0.3 : 1 }} onClick={() => !item.IsHold && handleWhatsApp(item.Id)}>
                              <FaWhatsapp size={16} />
                            </span>
                          </Tooltip>
                          {item.IsActive && !item.IsVerified && item.PaymentStatus === 'Pending' && (
                            <Tooltip title="Edit" arrow>
                              <span style={{ cursor: hasEditMasterPermission ? 'pointer' : 'not-allowed', fontSize: '14px', opacity: hasEditMasterPermission ? 1 : 0.4 }}
                                onClick={() => hasEditMasterPermission && handleOpenModal('edit', item.Id)}>✏️</span>
                            </Tooltip>
                          )}
                          {item.IsActive && !item.IsVerified && item.PaymentStatus === 'Pending' && (
                            <Tooltip title="Delete" arrow>
                              <span style={{ cursor: hasDeleteMasterPermission ? 'pointer' : 'not-allowed', fontSize: '14px', opacity: hasDeleteMasterPermission ? 1 : 0.4 }}
                                onClick={() => hasDeleteMasterPermission && handleOpenDeleteModal(item.Id, item.BillNumber)}>🗑️</span>
                            </Tooltip>
                          )}
                          {!item.IsActive && (
                            <Tooltip title="Activate" arrow>
                              <span style={{ cursor: hasEditMasterPermission ? 'pointer' : 'not-allowed', fontSize: '14px', opacity: hasEditMasterPermission ? 1 : 0.4 }}
                                onClick={() => hasEditMasterPermission && handleOpenActivateModal(item.Id, item.BillNumber)}>✅</span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
                      No sales found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          startIndex={startIndex}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>

      {/* Sale Modal */}
      <SaleModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        formData={formData}
        setFormData={setFormData}
        saleDetails={saleDetails}
        setSaleDetails={setSaleDetails}
        saleBundleDetails={saleBundleDetails}
        setSaleBundleDetails={setSaleBundleDetails}
        saleStockBranchDetails={saleStockBranchDetails}
        onSave={handleSave}
        onVerify={handleOpenVerifyModal}
        customers={customers}
        items={items}
        banks={banks}
        loading={modalLoading}
        freightPackingGST={freightPackingGST}
        companyId={companyId}
        branchId={branchId}
        token={token}
        initialInvoiceTypeId={formData.InvoiceTypeId || null}
        companyData={companyData}
        onCustomerChange={(masterId) => fetchItems(masterId || 0)}
      />

      {/* Verify Sale Modal */}
      <VerifySaleModal
        open={verifyModalOpen}
        onClose={handleCloseVerifyModal}
        formData={verifySaleData}
        saleDetails={verifySaleDetails}
        onVerify={handleVerify}
        onActualVerify={handleActualVerify}
        customers={customers}
        items={items}
        loading={modalLoading}
      />

      {/* Converted SO List Modal */}
      <Dialog
        open={convertedSOModalOpen}
        onClose={handleCloseConvertedSOModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh', width: '95vw', maxWidth: '1600px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f5f3ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: '#7c3aed', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Converted Sale Orders
              </Typography>
              <Chip label={`${convertedSOList.length} SOs`} size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#ede9fe', color: '#7c3aed' }} />
            </Box>
            <IconButton onClick={handleCloseConvertedSOModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: '#ede9fe' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>#</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#5b21b6' }}>SO Number</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>SO Date</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#5b21b6' }}>Customer</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#5b21b6' }}>Total Amount</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>Status</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {convertedSOList.map((so, index) => (
                    <tr key={so.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#faf5ff' }}>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{index + 1}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#7c3aed' }}>{so.SaleOrderPrefix || '-'}/{so.SaleOrderNumber || '-'}/{so.FinYear || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{formatDate(so.SaleOrderDate)}</td>
                      <td style={{ padding: '10px 12px' }}>{so.CustomerName || customers.find(c => (c.MasterId || c.Id) === so.MasterId)?.Name || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(so.TotalAmount)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <Chip
                          label={so.IsConverted ? 'Converted' : 'Pending'}
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            height: '20px',
                            bgcolor: so.IsConverted ? '#d1fae5' : '#fef3c7',
                            color: so.IsConverted ? '#065f46' : '#92400e'
                          }}
                        />
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="To Purchase">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleOpenSOToSaleModal(so)}
                              sx={{
                                fontSize: '0.65rem',
                                py: 0.25,
                                px: 1,
                                minWidth: 'auto',
                                bgcolor: '#10b981',
                                '&:hover': { bgcolor: '#059669' }
                              }}
                            >
                              To Sale
                            </Button>
                          </Tooltip>
                        </Box>
                      </td>
                    </tr>
                  ))}

                  {convertedSOList.length === 0 && (
                    <tr>
                      <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                        <Assignment sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                        <Typography variant="body2" color="textSecondary">No converted Sale orders found</Typography>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f5f3ff' }}>
          <Button onClick={handleCloseConvertedSOModal} variant="outlined" size="small" sx={{ borderColor: '#7c3aed', color: '#7c3aed', '&:hover': { borderColor: '#6d28d9', bgcolor: '#ede9fe' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Status Popup */}
      <Dialog
        open={Boolean(paymentPopupAnchor)}
        onClose={() => { setPaymentPopupAnchor(null); setPaymentPopupData(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxWidth: '420px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
              Receipt Details
            </Typography>
            <IconButton onClick={() => { setPaymentPopupAnchor(null); setPaymentPopupData(null); }} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {paymentPopupData && (() => {
            const receiptIds = paymentPopupData.ReceiptId ? String(paymentPopupData.ReceiptId).split(',').map(s => s.trim()).filter(s => s && s !== '0') : [];
            const voucherNos = paymentPopupData.ReceiptVoucherNo ? String(paymentPopupData.ReceiptVoucherNo).split(',').map(s => s.trim()).filter(Boolean) : [];
            const paidAmounts = paymentPopupData.PaidAmount ? String(paymentPopupData.PaidAmount).split(',').map(s => parseFloat(s.trim()) || 0) : [];

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {receiptIds.length > 0 ? (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                      Receipt Vouchers ({receiptIds.length})
                    </Typography>
                    {receiptIds.map((id, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: '#e0f2fe' }, transition: 'background 0.15s' }}
                        onClick={() => handleOpenReceiptModal(id)}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '12px', textDecoration: 'underline', cursor: 'pointer' }}>
                          {voucherNos[idx] || `RPT/${id}`}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669', fontSize: '12px' }}>
                          {paidAmounts[idx] ? `₹${paidAmounts[idx].toLocaleString('en-IN')}` : ''}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 0.5 }} />
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f0fdf4', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>Receipt Voucher No</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', fontSize: '12px' }}>
                      {paymentPopupData.PaymentStatus === 'Paid' ? 'Direct Sale' : (paymentPopupData.ReceiptVoucherNo || '-')}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>Payment Mode</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#7c3aed' }}>
                    {paymentPopupData.PaymentMode || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>Total Paid</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>
                    {formatCurrency(paymentPopupData.PaidAmount ? String(paymentPopupData.PaidAmount).split(',').reduce((s, v) => s + (parseFloat(v) || 0), 0) : 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>Pending Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                    {formatCurrency(paymentPopupData.PendingAmount || 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>Payment Status</Typography>
                  <Chip
                    label={paymentPopupData.PaymentStatus || '-'}
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor: paymentPopupData.PaymentStatus === 'Paid' ? '#d1fae5' : paymentPopupData.PaymentStatus === 'Partial' ? '#fef3c7' : '#fee2e2',
                      color: paymentPopupData.PaymentStatus === 'Paid' ? '#065f46' : paymentPopupData.PaymentStatus === 'Partial' ? '#92400e' : '#991b1b'
                    }}
                  />
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Receipt View Modal */}
      {receiptViewModal.open && !receiptViewLoading && (
        <PaymentReceiptsModal
          open={receiptViewModal.open}
          onClose={() => { setReceiptViewModal({ open: false }); setReceiptViewFormData({}); setReceiptViewDetails([]); }}
          modalAction="view"
          formData={receiptViewFormData}
          setFormData={setReceiptViewFormData}
          paymentReceiptDetails={receiptViewDetails}
          setPaymentReceiptDetails={setReceiptViewDetails}
          onSave={() => {}}
          masters={[{ Id: receiptViewFormData.MasterId, Name: receiptViewFormData.MasterName }]}
          banks={[]}
          purchases={receiptViewFormData.IsType ? receiptViewDetails : []}
          sales={!receiptViewFormData.IsType ? receiptViewDetails : []}
          paymentMethods={[]}
          gstRates={[]}
          loading={receiptViewLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
          fetchPurchases={() => {}}
          fetchSales={() => {}}
          fetchBillsForMaster={() => {}}
          fetchMaxBillNo={() => {}}
          fromDate={fromDate}
          toDate={toDate}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete sale <strong>{deleteItemNo}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog open={activateModalOpen} onClose={handleCloseActivateModal}>
        <DialogTitle>Confirm Activate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to activate sale <strong>{activateItemNo}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActivateModal}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* SO to Sale Create Modal */}
      <Dialog
        open={soToSaleModalOpen}
        onClose={handleCloseSOToSaleModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh', maxWidth: '1600px', width: '95vw' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#10b981', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Create Sale from SO
              </Typography>
              {/* {selectedSOData && (
                <Chip
                  label={`${soDetailsData.SaleOrderNo || '-'}`}
                  size="small"
                  onClick={() => handleOpenSODetailsModal(selectedSOData)}
                  sx={{
                    fontSize: '0.65rem',
                    height: '20px',
                    bgcolor: '#dbeafe',
                    color: '#1e40af'
                  }}
                />

              )} */}
            </Box>
            <IconButton onClick={handleCloseSOToSaleModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5 }}>
          {selectedSOData && (
            <>
              {/* Sale Information Section */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 0.5, display: 'block' }}>Sale Information</Typography>
              <Grid container spacing={0.75} sx={{ mb: 1 }} alignItems="flex-end">
                <Grid item xs={6} sm={1.5}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Bill No *</Typography>
                  <input
                    type="text"
                    value={soToSaleFormData.BillNumber || ''}
                    //onChange={(e) => handleSOToSalesFormChange('BillNumber', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: `1px solid ${!soToSaleFormData.BillNumber?.trim() ? '#ef4444' : '#d1d5db'}`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: !soToSaleFormData.BillNumber?.trim() ? '#fef2f2' : 'white'
                    }}
                    placeholder="Enter Bill No"
                    required
                  />
                </Grid>
                <Grid item xs={6} sm={1.3}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25 }}>Bill Date *</Typography>
                  <input
                    type="date"
                    value={soToSaleFormData.BillDate || ''}
                    //onChange={(e) => handlePOToPurchaseFormChange('BillDate', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: `1px solid ${!soToSaleFormData.BillDate ? '#ef4444' : '#d1d5db'}`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: !soToSaleFormData.BillDate ? '#fef2f2' : 'white'
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Customer *</Typography>
                  <input
                    type="text"
                    value={selectedSOData.MasterName || ''}
                    disabled
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', backgroundColor: '#f9fafb', color: '#5D4037', fontWeight: 700 }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>GST No</Typography>
                  <input
                    type="text"
                    value={customers.find(s => (s.MasterId || s.Id) === selectedSOData.MasterId)?.GSTNumber || customers.find(s => (s.MasterId || s.Id) === selectedSOData.MasterId)?.GSTNo || '-'}
                    disabled
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', backgroundColor: '#f0fdf4', color: '#5D4037', fontWeight: 700 }}
                  />
                </Grid>
                <Grid item xs={6} sm={1}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Inv Type</Typography>
                  <input
                    type="text"
                    value={customers.find(s => (s.MasterId || s.Id) === selectedSOData.MasterId)?.InvoiceTypeName || customers.find(s => (s.MasterId || s.Id) === selectedSOData.MasterId)?.InvoiceType || '-'}
                    disabled
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', backgroundColor: '#fef3c7', color: '#5D4037', fontWeight: 700 }}
                  />
                </Grid>
                <Grid item xs={6} sm={1.5}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>GST Type *</Typography>
                  <select
                    value={soToSaleFormData.GstCalculationType ?? 1}
                    onChange={(e) => {
                      const gstType = Number(e.target.value);

                      const gstTypeName =
                        gstType === 1
                          ? 'Excluding Tax'
                          : gstType === 2
                            ? 'Including Tax'
                            : '';

                      //handlePOToPurchaseFormChange('GstCalculationType', gstType);
                      //handlePOToPurchaseFormChange('GstCalculationTypeName', gstTypeName);
                    }}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: '#e0f2fe',
                    }}
                  >
                    <option value={1}>Excluding Tax</option>
                    <option value={2}>Including Tax</option>
                  </select>
                </Grid>
                <Grid item xs={6} sm={1.2}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Clearing Date</Typography>
                  <input
                    type="date"
                    value={soToSaleFormData.ClearingDate || new Date().toISOString().split('T')[0]}
                    //onChange={(e) => handlePOToPurchaseFormChange('ClearingDate', e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                  />
                </Grid>
              </Grid>

              {/* Items Table */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981' }}>Items ({selectedSODetails.length} items)</Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => {
                    const newItem = {
                      Id: 0,
                      ItemId: '',
                      ItemName: '',
                      HSNCode: '',
                      Quantity: 0,
                      ReceivedQuantity: 0,
                      Rate: 0,
                      Amount: 0,
                      DiscountPercentage: 0,
                      DiscountAmount: 0,
                      GSTRate: 0,
                      TaxableAmount: 0,
                      CGSTAmount: 0,
                      SGSTAmount: 0,
                      IGSTAmount: 0,
                      TotalAmount: 0,
                      DamageQuantity: 0,
                      StockQuantity: 0
                    };
                    const updatedDetails = [...selectedSODetails, newItem];
                    setSelectedSODetails(updatedDetails);
                    // Recalculate totals after adding new item
                    calculateSOTotals(updatedDetails);
                  }}
                  sx={{
                    bgcolor: '#10b981',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: '24px',
                    '&:hover': { bgcolor: '#059669' }
                  }}
                >
                  Add Item
                </Button>
              </Box>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#d1fae5' }}>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>#</th>
                        <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600, color: '#065f46' }}>Item</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>HSN</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>Order Qty</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>Rcvd Qty</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>Dis %</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Dis Amt</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Rate</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>GST%</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Taxable</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>GST Amt</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Total</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSODetails.map((detail, index) => (
                        <tr key={detail.Id || index} style={{
                          borderBottom: '1px solid #e2e8f0',
                          background: index % 2 === 0 ? '#fff' : '#f0fdf4',
                          backgroundColor: (!detail.ItemId || !detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0 || !detail.Rate || parseFloat(detail.Rate) <= 0) ? '#fef2f2' : (index % 2 === 0 ? '#fff' : '#f0fdf4')
                        }}>
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '4px 6px', fontWeight: 500, color: 'darkred', fontSize: '0.8rem' }}>
                            {detail.ItemName || '-'}
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center', fontSize: '11px', color: '#6b7280' }}>
                            {detail.HSNCode || '-'}
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <input
                              type="number"
                              value={detail.Quantity || ''}
                              //onChange={(e) => handlePOToPurchaseDetailChange(index, 'Quantity', e.target.value)}
                              style={{ width: '60px', padding: '3px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                              min="0"
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <input
                              type="number"
                              value={detail.ReceivedQuantity || ''}
                              //onChange={(e) => handlePOToPurchaseDetailChange(index, 'ReceivedQuantity', e.target.value)}
                              onKeyPress={(e) => {
                                // Prevent non-numeric characters except decimal point
                                if (!/[0-9.]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
                                  e.preventDefault();
                                }
                              }}
                              onPaste={(e) => {
                                // Prevent pasting invalid content
                                const paste = (e.clipboardData || window.clipboardData).getData('text');
                                if (!/^[0-9]*\.?[0-9]*$/.test(paste)) {
                                  e.preventDefault();
                                }
                              }}
                              style={{
                                width: '60px',
                                padding: '3px',
                                border: `1px solid ${!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0 ? '#ef4444' : '#d1d5db'}`,
                                borderRadius: '4px',
                                textAlign: 'center',
                                fontSize: '12px',
                                backgroundColor: (!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0) ? '#fef2f2' : 'white'
                              }}
                              min="0"
                              step="0.01"
                              required
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <input
                              type="number"
                              value={detail.DiscountPercentage || 0}
                              //onChange={(e) => handlePOToPurchaseDetailChange(index, 'DiscountPercentage', e.target.value)}
                              style={{ width: '60px', padding: '3px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                              min="0"
                              max="100"
                              step="0.01"
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                            <input
                              type="number"
                              value={detail.DiscountAmount || 0}
                              //onChange={(e) => handlePOToPurchaseDetailChange(index, 'DiscountAmount', e.target.value)}
                              style={{ width: '80px', padding: '3px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'right', fontSize: '12px' }}
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                            <input
                              type="number"
                              value={detail.Rate || ''}
                              //onChange={(e) => handlePOToPurchaseDetailChange(index, 'Rate', e.target.value)}
                              onKeyPress={(e) => {
                                // Prevent non-numeric characters except decimal point
                                if (!/[0-9.]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
                                  e.preventDefault();
                                }
                              }}
                              onPaste={(e) => {
                                // Prevent pasting invalid content
                                const paste = (e.clipboardData || window.clipboardData).getData('text');
                                if (!/^[0-9]*\.?[0-9]*$/.test(paste)) {
                                  e.preventDefault();
                                }
                              }}
                              style={{
                                width: '80px',
                                padding: '3px',
                                border: `1px solid ${!detail.Rate || parseFloat(detail.Rate) <= 0 ? '#ef4444' : '#d1d5db'}`,
                                borderRadius: '4px',
                                textAlign: 'right',
                                fontSize: '12px',
                                backgroundColor: (!detail.Rate || parseFloat(detail.Rate) <= 0) ? '#fef2f2' : 'white'
                              }}
                              min="0"
                              step="0.01"
                              required
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#059669' }}>{detail.GSTRate || 0}%</span>
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(detail.TaxableAmount || 0)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>{formatCurrency((detail.CGSTAmount || 0) + (detail.SGSTAmount || 0) + (detail.IGSTAmount || 0))}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{formatCurrency(detail.TotalAmount || 0)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updatedDetails = selectedSODetails.filter((_, i) => i !== index);
                                setSelectedSODetails(updatedDetails);
                                calculateSOTotals(updatedDetails);
                              }}
                              sx={{ color: '#ef4444', p: 0.5 }}
                            >
                              <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                      {selectedSODetails.length === 0 && (
                        <tr>
                          <td colSpan="13" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Additional Charges & Summary Layout */}
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {/* Additional Charges Section */}
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 0.5, display: 'block' }}>Additional Charges</Typography>
                  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 1, bgcolor: '#f9fafb' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Freight</Typography>
                        <input
                          type="number"
                          value={soToSaleFormData.FreightAmount || ''}
                          //onChange={(e) => handlePOToPurchaseFormChange('FreightAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Packing</Typography>
                        <input
                          type="number"
                          value={soToSaleFormData.PackingAmount || ''}
                          //onChange={(e) => handlePOToPurchaseFormChange('PackingAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>TCS Amount</Typography>
                        <input
                          type="number"
                          value={soToSaleFormData.TCSAmount || ''}
                          //onChange={(e) => handlePOToPurchaseFormChange('TCSAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Paid Amount</Typography>
                        <input
                          type="number"
                          value={soToSaleFormData.UPIAmount || ''}
                          //onChange={(e) => handlePOToPurchaseFormChange('PaidAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Paid Amount</Typography>
                        <input
                          type="number"
                          value={soToSaleFormData.CashAmount || ''}
                          //onChange={(e) => handlePOToPurchaseFormChange('PaidAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Paid Amount</Typography>
                        <input
                          type="number"
                          value={soToSaleFormData.CardAmount || ''}
                          //onChange={(e) => handlePOToPurchaseFormChange('PaidAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Remarks</Typography>
                        <textarea
                          value={soToSaleFormData.AdditionalRemarks || ''}
                          //onChange={(e) => handlePOToPurchaseFormChange('AdditionalRemarks', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', minHeight: '60px', resize: 'vertical' }}
                          placeholder="Enter remarks..."
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Summary Section */}
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 0.5, display: 'block' }}>Summary</Typography>
                  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 1, bgcolor: '#f0fdf4' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Subtotal</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.Amount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Discount</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#dc2626', fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.DiscountAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Taxable</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.TaxableAmount || 0)}</Typography>
                    </Box>
                    {/* Show IGST or CGST/SGST based on invoice type */}
                    {(() => {
                      const poInvoiceType = selectedSOData?.InvoiceTypeName?.toLowerCase() || '';
                      const formInvoiceType = soToSaleFormData?.InvoiceTypeName?.toLowerCase() || '';
                      const hasIGSTAmount = (soToSaleFormData.IGSTAmount || 0) > 0;
                      const hasNoCGSTSGST = (soToSaleFormData.CGSTAmount || 0) === 0 && (soToSaleFormData.SGSTAmount || 0) === 0;
                      const itemsHaveIGST = selectedSODetails.some(item => (item.IGSTAmount || 0) > 0);
                      const isIGST = poInvoiceType.includes('igst') || formInvoiceType.includes('igst') || (hasIGSTAmount && hasNoCGSTSGST) || (hasIGSTAmount > 0) || itemsHaveIGST;

                      return isIGST ? (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>IGST</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.IGSTAmount || 0)}</Typography>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>CGST</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.CGSTAmount || 0)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>SGST</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.SGSTAmount || 0)}</Typography>
                          </Box>
                        </>
                      );
                    })()}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Freight</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.FreightAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Freight GST ({Math.round(soToSaleFormData.FreightGSTRate || 0)}%)</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.FreightGSTAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Packing</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.PackingAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Packing GST ({Math.round(soToSaleFormData.PackingGSTRate || 0)}%)</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.PackingGSTAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>TCS</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(soToSaleFormData.TCSAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Round Off</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{(soToSaleFormData.RoundOff || 0) >= 0 ? '+' : ''}{(soToSaleFormData.RoundOff || 0).toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.8rem' }}>Total</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.85rem' }}>{formatCurrency(soToSaleFormData.TotalAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Paid</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#10b981', fontSize: '0.75rem' }}>{formatCurrency((parseFloat(soToSaleFormData.CardAmount) || 0) + (parseFloat(soToSaleFormData.CashAmount) || 0) + (parseFloat(soToSaleFormData.UPIAmount) || 0))}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2, borderTop: '1px solid #d1fae5', pt: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>Balance</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.85rem' }}>{formatCurrency(Math.max(0, (soToSaleFormData.TotalAmount || 0) - (parseFloat(soToSaleFormData.CardAmount) || 0) - (parseFloat(soToSaleFormData.UPIAmount) || 0) - (parseFloat(soToSaleFormData.CashAmount) || 0)))}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
          <Button onClick={handleCloseSOToSaleModal} variant="outlined" size="small" sx={{ borderColor: '#10b981', color: '#10b981', '&:hover': { borderColor: '#059669', bgcolor: '#d1fae5' } }}>
            Cancel
          </Button>
          <Button
            //onClick={handleSaveSOToSale}
            variant="contained" size="small" disabled={loading} sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
            Create Sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* PO Details View Modal */}
      <Dialog
        open={soDetailsModalOpen}
        onClose={handleCloseSODetailsModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh', maxWidth: '1000px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0f9ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: '#0ea5e9', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Sale Order Details
              </Typography>
              {soDetailsData && (
                <Chip
                  label={`${soDetailsData.SaleOrderPrefix || '-'} / ${soDetailsData.SaleOrderNumber || '-'} / ${soDetailsData.FinYear || '-'}`}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: '20px',
                    bgcolor: '#dbeafe',
                    color: '#1e40af'
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleCloseSODetailsModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {soDetailsData && (
            <>
              {/* PO Header Information */}
              <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1, mb: 2, border: '1px solid #dbeafe' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>SO Number</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af' }}>{soDetailsData.SaleOrderPrefix || '-'}/{soDetailsData.SaleOrderNumber || '-'}/{soDetailsData.FinYear || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>SO Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(soDetailsData.SaleOrderDate)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>{soDetailsData.MasterName || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>{formatCurrency(soDetailsData.TotalAmount)}</Typography>
                  </Grid>

                  {/* Second Row */}
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Remarks</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      {soDetailsData.Remarks || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Instruction 1</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      {soDetailsData.Instruction1 || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Instruction 2</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      {soDetailsData.Instruction2 || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* PO Items Table */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#0ea5e9', mb: 1, display: 'block' }}>
                SO Items ({soDetailsItems.length} items)
              </Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#dbeafe' }}>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#1e40af' }}>Item Name</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>Quantity</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Rate</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Amount</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>GST%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>GST Amount</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {soDetailsItems.map((item, index) => (
                        <tr key={item.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '8px', fontWeight: 500 }}>{item.ItemName || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{item.Quantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(item.Rate || 0)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(item.Amount || 0)}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#dc2626' }}>{item.GSTRate || 0}%</span>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>
                            {formatCurrency((item.CGSTAmount || 0) + (item.SGSTAmount || 0) + (item.IGSTAmount || 0))}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(item.TotalAmount || 0)}</td>
                        </tr>
                      ))}
                      {soDetailsItems.length === 0 && (
                        <tr>
                          <td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* PO Summary */}
              <Box sx={{ p: 1, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #dbeafe' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#0ea5e9', mb: 0.5, display: 'block' }}>SO Summary</Typography>
                <Grid container spacing={1}>
                  {/* First Row */}
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Subtotal</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.Amount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Discount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.DiscountAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Taxable</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.TaxableAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>GST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency((soDetailsData.CGSTAmount || 0) + (soDetailsData.SGSTAmount || 0) + (soDetailsData.IGSTAmount || 0))}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Freight</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.FreightAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Second Row */}
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Freight GST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.FreightGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Packing</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.PackingAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Packing GST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.PackingGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>TCS</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.TCSAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#dbeafe', borderRadius: 1, border: '1px solid #93c5fd' }}>
                      <Typography variant="caption" sx={{ color: '#1e40af', display: 'block', mb: 0.2, fontWeight: 600, fontSize: '0.7rem' }}>Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e40af', fontSize: '0.85rem' }}>
                        {formatCurrency(soDetailsData.TotalAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Third Row - Additional Details */}
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>CGST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.CGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>SGST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.SGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>IGST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.IGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Round Off</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {(soDetailsData.RoundOff || 0) >= 0 ? '+' : ''}{(soDetailsData.RoundOff || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Paid Amount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.8rem' }}>
                        {formatCurrency(soDetailsData.PaidAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f0f9ff' }}>
          <Button onClick={handleCloseSODetailsModal} variant="contained" size="small" sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Orders Modal — Confirmed Enquiries */}
      <Dialog open={ordersModalOpen} onClose={() => setOrdersModalOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)', px: 2.5, py: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#fff', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>Confirmed Orders</Typography>
              {!ordersLoading && (
                <Chip label={confirmedOrders.length} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, fontSize: '0.72rem', height: 20 }} />
              )}
            </Box>
            <IconButton size="small" onClick={() => setOrdersModalOpen(false)}
              sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              <Close />
            </IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {ordersLoading ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>Loading orders...</Typography>
            </Box>
          ) : confirmedOrders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography sx={{ fontSize: '2rem', mb: 1 }}>📋</Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>No confirmed orders found.</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fffbeb' }}>
                    {['#', 'Enquiry No', 'Date', 'Customer', 'Contact', 'Amount', ''].map((h, i) => (
                      <TableCell key={i} sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.4px', py: 1, px: 1.5, textAlign: i === 5 ? 'right' : 'left' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {confirmedOrders.map((item, idx) => (
                    <TableRow key={item.Id} sx={{ '&:nth-of-type(even)': { bgcolor: '#fffbeb' }, '&:hover': { bgcolor: '#fef3c7' } }}>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5, color: '#64748b' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', py: 0.75, px: 1.5, fontWeight: 600, color: '#1e293b' }}>
                        {item.EnquiryNo || `${item.EnquiryPrefix || ''}/${item.EnquiryNumber || ''}`}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5, color: '#475569' }}>
                        {item.EnquiryDate ? new Date(item.EnquiryDate).toLocaleDateString('en-IN') : '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5, color: '#1e293b' }}>{item.CustomerName || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5, color: '#475569' }}>{item.ContactNumber || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', py: 0.75, px: 1.5, fontWeight: 700, color: '#059669', textAlign: 'right' }}>
                        ₹{(parseFloat(item.TotalAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right' }}>
                        <Button size="small" variant="contained"
                          onClick={() => handleConvertToSale(item)}
                          sx={{ fontSize: '0.7rem', py: 0.4, px: 1.2, textTransform: 'none', bgcolor: '#2C3E6E', '&:hover': { bgcolor: '#1a2d5a' }, borderRadius: '6px', whiteSpace: 'nowrap' }}>
                          To Sale
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #fef3c7', bgcolor: '#fffbeb' }}>
          <Button onClick={() => setOrdersModalOpen(false)} variant="outlined" size="small"
            sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#d97706', color: '#d97706' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default SaleIndex;

